import React, { useEffect, useRef, useState } from "react";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Slider } from "@/components/ui/slider";
import { getOrgInfo, loadOrgTheme } from "@/theme";
import { HighTrafficLoading } from "@/components/LoadingSpinner";
import { ParticipationConsent, TermsDialog } from "@/components/LegalContent";
import { personalisationVisible } from "@/lib/widgetConfig";

// Default subtitles for each campaign type
const DEFAULT_SUBTITLES = {
  set_and_send: "Your voice matters. Send a personalised message to decision-makers in just a few clicks.",
  mp_ping: "Find your local representative and let them know how you feel about the issues that matter to you.",
  sign_and_send: "Join thousands of others. Add your name and send a message directly to those in power.",
  submission_builder: "Make your voice heard. We'll help you write a professional submission to the inquiry.",
  social_surge: "Amplify your impact. Share this campaign with your network on social media."
};

import { useNavigate } from "react-router-dom";

// Ensure selected Google fonts are loaded
function ensureGoogleFontLoaded(fontName){
  if(!fontName) return;
  const fam = String(fontName).trim();
  const id = `gf-${fam.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id; link.rel = 'stylesheet';
  // Use + for spaces (Google Fonts expects this format)
  const familyParam = fam.replace(/\s+/g, '+');
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

// Build a properly-quoted font-family value with system fallbacks.
// Without quoting, multi-word fonts like "Space Grotesk" silently fail in
// some browsers (or fall back to Times). Always pass through this helper.
function fontStack(name){
  const n = (name || 'Inter').trim().replace(/"/g, '');
  return `"${n}", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Click-to-edit primitives for the live widget preview.
// `EditableText` renders the value as styled text; clicking it swaps in a
// single-line input overlaid in place. Pressing Enter / clicking outside saves;
// Escape cancels.
// `EditableTextarea` is the same idea but for longer/multi-line copy.
// ─────────────────────────────────────────────────────────────────────────────
export function EditableText({ value, onSave, placeholder = "Click to edit…", className = "", style = {}, multiline = false, testId, ariaLabel }){
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || "");
  const inputRef = React.useRef(null);

  React.useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const commit = () => {
    const next = (draft || "").trimEnd();
    setEditing(false);
    if (next !== (value || "")) onSave(next);
  };
  const cancel = () => { setDraft(value || ""); setEditing(false); };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); return; }
    if (!multiline && e.key === "Enter") { e.preventDefault(); commit(); return; }
    if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
  };

  if (editing) {
    const sharedProps = {
      ref: inputRef,
      value: draft,
      onChange: (e) => setDraft(e.target.value),
      onBlur: commit,
      onKeyDown: onKey,
      className: `${className} bg-white/95 outline-none ring-2 ring-[#1E3A8A]/40 rounded px-1 -mx-1 w-full`,
      style: { textAlign: 'inherit', ...style, minWidth: "5ch" },
      "data-testid": testId ? `${testId}-input` : undefined,
      "aria-label": ariaLabel,
    };
    return multiline
      ? <textarea {...sharedProps} rows={Math.min(6, Math.max(2, (draft || "").split("\n").length))} />
      : <input type="text" {...sharedProps} />;
  }

  const showPlaceholder = !value || !value.toString().trim();
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); } }}
      className={`${className} cursor-text rounded px-1 -mx-1 hover:bg-[#1E3A8A]/5 hover:outline hover:outline-1 hover:outline-dashed hover:outline-[#1E3A8A]/30 transition-colors inline-block`}
      style={style}
      title="Click to edit"
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {showPlaceholder
        ? <span className="text-gray-400 italic">{placeholder}</span>
        : (value || "").split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line || "\u00A0"}
            </React.Fragment>
          ))}
    </span>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// Design drawer — right-side panel with 5 element-specific tabs.
// Driven by a `panel` prop ('logo' | 'title' | 'fields' | 'button' | 'bg').
// ─────────────────────────────────────────────────────────────────────────────

// NOTE: these tiny presentational components are defined at module scope ON
// PURPOSE. If they live inside DesignDrawer they get a fresh function identity
// on every render — React then treats them as a brand-new component type and
// unmounts/remounts their entire subtree, which kills mid-drag interactions on
// the native <input type="range"> thumb and the OS <input type="color"> dialog.
const DrawerLabel = ({ children }) => <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{children}</div>;
const DrawerRow = ({ children }) => <div className="space-y-1">{children}</div>;
const DrawerColorPicker = ({ value, onChange: oc, allowEmpty }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value || '#000000'} onChange={(e) => oc(e.target.value)} className="h-8 w-12 rounded border border-gray-300 cursor-pointer" />
    <input type="text" value={value || ''} onChange={(e) => oc(e.target.value)} placeholder={allowEmpty ? 'transparent' : ''} className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5 font-mono" />
    {allowEmpty && value ? (
      <button type="button" onClick={() => oc('')} className="text-xs text-gray-500 hover:text-red-600">Clear</button>
    ) : null}
  </div>
);
const DrawerAlignPicker = ({ value, onChange: oc }) => (
  <div className="grid grid-cols-3 gap-1">
    {['left','center','right'].map(a => (
      <button key={a} type="button" onClick={() => oc(a)} data-testid={`align-${a}`}
        className={`px-2 py-1.5 text-xs border rounded ${value === a ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
        {a[0].toUpperCase() + a.slice(1)}
      </button>
    ))}
  </div>
);
const DrawerSizePicker = ({ value, onChange: oc, options }) => (
  <div className="flex gap-1 flex-wrap">
    {options.map(s => (
      <button key={s.value} type="button" onClick={() => oc(s.value)}
        className={`px-2 py-1.5 text-xs border rounded ${value === s.value ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
        {s.label}
      </button>
    ))}
  </div>
);

export function DesignDrawer({ open, panel, onClose, theme, onChange, buttonStyle, onButtonStyleChange, subtitle, showSubtitle, onPatch }){
  const t = theme || {};
  const set = (key, value) => onChange({ ...t, [key]: value });
  const setBtn = (key, value) => onButtonStyleChange({ ...(buttonStyle || {}), [key]: value });
  const patch = typeof onPatch === 'function' ? onPatch : () => {};

  const TABS = [
    { id: 'brand',  label: 'Brand' },
    { id: 'logo',   label: 'Logo' },
    { id: 'title',  label: 'Title' },
    { id: 'fields', label: 'Fields' },
    { id: 'button', label: 'Button' },
    { id: 'bg',     label: 'Background' },
  ];

  const FONT_CHOICES = ['Inter','Manrope','Space Grotesk','Work Sans','Source Code Pro','Roboto Mono','EB Garamond','Fira Sans','Nunito','Chivo','Playfair Display','Lora','Merriweather','Poppins'];

  // Image upload for background — uses the existing /my-org/logo endpoint as
  // a general "upload an image" backend; the returned URL is stored in theme.bgImage.
  const fileInputRef = React.useRef(null);
  const onPickBg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) set('bgImage', r.data.url);
    } catch (err) {
      console.error(err);
      alert('Image upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const onDropBg = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) set('bgImage', r.data.url);
    } catch (err) { alert('Image upload failed'); }
  };

  if (!open) return null;

  // Local aliases so existing JSX below doesn't need a global find/replace.
  const Label = DrawerLabel;
  const Row = DrawerRow;
  const ColorPicker = DrawerColorPicker;
  const AlignPicker = DrawerAlignPicker;
  const SizePicker = DrawerSizePicker;

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-80 bg-white border-l border-gray-200 shadow-2xl flex flex-col" data-testid="design-drawer">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="font-semibold text-gray-900">Design</div>
        <button onClick={onClose} data-testid="drawer-close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
      </div>
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => onChange.__setPanel?.(tab.id)} data-testid={`drawer-tab-${tab.id}`}
            className={`flex-shrink-0 px-3 py-2 text-xs font-medium border-b-2 ${panel === tab.id ? 'border-[#1E3A8A] text-[#1E3A8A]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">

        {panel === 'brand' && (
          <>
            <Row><Label>Body font</Label>
              <select className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" value={t.font || 'Inter'} onChange={e => set('font', e.target.value)} data-testid="brand-body-font">
                {FONT_CHOICES.map(f => (<option key={f} value={f}>{f}</option>))}
              </select>
            </Row>
            <Row><Label>Title font</Label>
              <select className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" value={t.font_title || t.font || 'Inter'} onChange={e => set('font_title', e.target.value)} data-testid="brand-title-font">
                {FONT_CHOICES.map(f => (<option key={f} value={f}>{f}</option>))}
              </select>
            </Row>
            <hr className="border-gray-200" />
            <Row><Label>Primary colour (buttons, accents)</Label><ColorPicker value={t.primary || '#0a2a66'} onChange={v => set('primary', v)} /></Row>
            <Row><Label>Primary text (on buttons)</Label><ColorPicker value={t.primaryText || '#ffffff'} onChange={v => set('primaryText', v)} /></Row>
            <Row><Label>Card background</Label><ColorPicker value={t.bg || '#ffffff'} onChange={v => set('bg', v)} /></Row>
            <Row><Label>Body text</Label><ColorPicker value={t.text || '#000000'} onChange={v => set('text', v)} /></Row>
            <Row><Label>Field background</Label><ColorPicker value={t.secondary || '#eef2ff'} onChange={v => set('secondary', v)} /></Row>
            <p className="text-xs text-gray-500 leading-relaxed pt-2">These are the global brand settings. Use the other tabs (or click any element in the preview) to fine-tune individual pieces.</p>
          </>
        )}

        {panel === 'logo' && (
          <>
            <Row><Label>Show logo</Label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={t.show_logo !== false} onChange={e => set('show_logo', e.target.checked)} /> Visible
              </label>
            </Row>
            <Row><Label>Alignment</Label><AlignPicker value={t.logoAlign || 'center'} onChange={v => set('logoAlign', v)} /></Row>
            <Row><Label>Size (height in px)</Label>
              <input type="range" min="20" max="120" step="2" value={t.logoSize || 40} onChange={e => set('logoSize', Number(e.target.value))} className="w-full" />
              <div className="text-xs text-gray-500">{t.logoSize || 40} px</div>
            </Row>
            <Row><Label>Drop shadow</Label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!t.logoShadow} onChange={e => set('logoShadow', e.target.checked)} /> Soft shadow under logo
              </label>
            </Row>
          </>
        )}

        {panel === 'title' && (
          <>
            <Row><Label>Show title</Label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={t.show_title !== false} onChange={e => set('show_title', e.target.checked)} /> Visible
              </label>
            </Row>
            <Row><Label>Title — Size</Label>
              <SizePicker value={t.titleSize || '2xl'} onChange={v => set('titleSize', v)}
                options={[{value:'lg',label:'S'},{value:'xl',label:'M'},{value:'2xl',label:'L'},{value:'3xl',label:'XL'},{value:'4xl',label:'XXL'}]} />
            </Row>
            <Row><Label>Title — Alignment</Label><AlignPicker value={t.titleAlign || 'center'} onChange={v => set('titleAlign', v)} /></Row>
            <Row><Label>Title — Colour</Label><ColorPicker value={t.titleColor || '#0a0a0a'} onChange={v => set('titleColor', v)} /></Row>
            <Row><Label>Title — Drop shadow</Label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!t.titleShadow} onChange={e => set('titleShadow', e.target.checked)} /> Soft shadow
              </label>
            </Row>
            <Row><Label>Title — Background (optional)</Label><ColorPicker value={t.titleBg || ''} onChange={v => set('titleBg', v)} allowEmpty /></Row>
            <hr className="border-gray-200" />
            <Row><Label>Subtitle</Label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" data-testid="drawer-show-subtitle" checked={!!showSubtitle} onChange={e => patch('show_subtitle', e.target.checked)} /> Show subtitle
              </label>
            </Row>
            {showSubtitle && (
              <Row><Label>Subtitle text</Label>
                <textarea
                  data-testid="drawer-subtitle-text"
                  value={subtitle || ''}
                  onChange={e => patch('subtitle', e.target.value)}
                  rows={3}
                  placeholder="A short line under the title — supports multiple lines"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-y"
                />
              </Row>
            )}
            <Row><Label>Subtitle — Size</Label>
              <SizePicker value={t.subtitleSize || 'sm'} onChange={v => set('subtitleSize', v)}
                options={[{value:'xs',label:'XS'},{value:'sm',label:'S'},{value:'base',label:'M'},{value:'lg',label:'L'}]} />
            </Row>
            <Row><Label>Subtitle — Alignment</Label><AlignPicker value={t.subtitleAlign || 'center'} onChange={v => set('subtitleAlign', v)} /></Row>
            <Row><Label>Subtitle — Colour</Label><ColorPicker value={t.subtitleColor || ''} onChange={v => set('subtitleColor', v)} allowEmpty /></Row>
          </>
        )}

        {panel === 'fields' && (
          <>
            <Row><Label>Field — Border colour</Label><ColorPicker value={t.fieldBorder || '#d1d5db'} onChange={v => set('fieldBorder', v)} /></Row>
            <Row><Label>Field — Interior colour</Label><ColorPicker value={t.fieldInterior || '#ffffff'} onChange={v => set('fieldInterior', v)} /></Row>
            <Row><Label>Field — Text colour</Label><ColorPicker value={t.fieldText || '#0a0a0a'} onChange={v => set('fieldText', v)} /></Row>
            <p className="text-xs text-gray-500 leading-relaxed">These apply to every input on both pages of the widget (personalisation field, name, email, signature address, and copy fields).</p>
          </>
        )}

        {panel === 'button' && (
          <>
            <Row><Label>Button — Background colour</Label><ColorPicker value={t.primary || '#0a2a66'} onChange={v => set('primary', v)} /></Row>
            <Row><Label>Button — Text colour</Label><ColorPicker value={t.primaryText || '#ffffff'} onChange={v => set('primaryText', v)} /></Row>
            <Row><Label>Corner radius</Label>
              <input type="range" min="0" max="32" step="1" value={(buttonStyle?.radius ?? 8)} onChange={e => setBtn('radius', Number(e.target.value))} className="w-full" />
              <div className="text-xs text-gray-500">{buttonStyle?.radius ?? 8} px</div>
            </Row>
            <Row><Label>Width</Label>
              <SizePicker value={buttonStyle?.width || 'full'} onChange={v => setBtn('width', v)}
                options={[{value:'auto',label:'Auto'},{value:'half',label:'Half'},{value:'full',label:'Full'}]} />
            </Row>
            <Row><Label>Alignment</Label><AlignPicker value={buttonStyle?.alignment || 'center'} onChange={v => setBtn('alignment', v)} /></Row>
          </>
        )}

        {panel === 'bg' && (
          <>
            <Row><Label>Background colour</Label><ColorPicker value={t.bg || '#ffffff'} onChange={v => set('bg', v)} /></Row>
            <Row><Label>Background image</Label>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDropBg}
                className="border-2 border-dashed border-gray-300 rounded p-3 text-xs text-gray-500 text-center hover:border-[#1E3A8A] hover:bg-[#1E3A8A]/5 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="bg-image-drop"
              >
                {t.bgImage ? (
                  <div className="space-y-2">
                    <img src={t.bgImage} alt="background" className="max-h-24 w-auto mx-auto rounded" />
                    <div className="text-[10px] text-gray-500 break-all">{t.bgImage}</div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); set('bgImage', ''); }} className="text-xs text-red-600 hover:underline" data-testid="bg-image-delete">Remove image</button>
                  </div>
                ) : (
                  <>Click or drop an image here</>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickBg} />
              </div>
            </Row>
            <Row><Label>Image blur</Label>
              <input type="range" min="0" max="20" step="1" value={t.bgBlur || 0} onChange={e => set('bgBlur', Number(e.target.value))} className="w-full" />
              <div className="text-xs text-gray-500">{t.bgBlur || 0} px</div>
            </Row>
            <Row><Label>Image opacity</Label>
              <input type="range" min="0.2" max="1" step="0.05" value={t.bgImageOpacity ?? 1} onChange={e => set('bgImageOpacity', Number(e.target.value))} className="w-full" />
              <div className="text-xs text-gray-500">{Math.round(((t.bgImageOpacity ?? 1) * 100))}%</div>
            </Row>
          </>
        )}
      </div>
    </div>
  );
}

// Pencil affordance shown on hover over an editable element. Clicking opens
// the design drawer to the relevant panel. Default position sits 8px OUTSIDE
// the right edge of the element so it never overlaps the content.
function EditAffordance({ panel, onOpen, label, position = "top-1/2 -translate-y-1/2 -right-8" }){
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(panel); }}
      title={label || `Edit ${panel}`}
      data-testid={`edit-${panel}`}
      className={`absolute ${position} bg-white border border-[#1E3A8A] text-[#1E3A8A] rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-[#1E3A8A] hover:text-white z-20`}
      aria-label={label || `Edit ${panel}`}
    >
      ✎
    </button>
  );
}



function RecipientPicker({ open, onClose, onApply }){
  const [rows,setRows]=useState([]);
  const [state,setState]=useState("");
  const [chamber,setChamber]=useState("");
  const [house,setHouse]=useState("");
  const [search,setSearch]=useState("");
  const [sel,setSel]=useState({});
  useEffect(()=>{ if(open){ (async()=>{ const r=await api.get('/owner/mp-directory'); setRows(r.data.rows||[]); })(); } },[open]);
  if(!open) return null;
  const filtered = rows.filter(r=> (!state || r.state === state) && (!chamber || r.level === chamber) && (!house || r.house === house) && (!search || Object.values(r||{}).join(' ').toLowerCase().includes(search.toLowerCase())));
  const toggle=(id)=> setSel(s=> ({...s, [id]: !s[id]}));
  const apply=()=>{ const emails = filtered.filter(r=> sel[r.id]).map(r=> r.email).filter(Boolean); onApply(emails); onClose(); };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="recipient-picker">
      <div className="bg-white w-full max-w-6xl rounded shadow-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Select Recipients</div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select data-testid="rp-state" className="border rounded px-3 py-2" value={state} onChange={(e)=>setState(e.target.value)}>
            <option value="">All States</option>
            <option value="ACT">ACT</option>
            <option value="NSW">NSW</option>
            <option value="NT">NT</option>
            <option value="QLD">QLD</option>
            <option value="SA">SA</option>
            <option value="TAS">TAS</option>
            <option value="VIC">VIC</option>
            <option value="WA">WA</option>
          </select>
          <select data-testid="rp-chamber" className="border rounded px-3 py-2" value={chamber} onChange={(e)=>setChamber(e.target.value)}>
            <option value="">Federal/State</option>
            <option value="federal">Federal</option>
            <option value="state">State</option>
          </select>
          <select data-testid="rp-house" className="border rounded px-3 py-2" value={house} onChange={(e)=>setHouse(e.target.value)}>
            <option value="">Upper/Lower</option>
            <option value="lower">Lower House</option>
            <option value="upper">Upper House</option>
          </select>
          <Input data-testid="rp-search" placeholder="Search" value={search} onChange={(e)=>setSearch(e.target.value)} />
        </div>
        <div className="overflow-auto border rounded max-h-80">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead className="min-w-[180px]">Name</TableHead>
                <TableHead className="w-16">State</TableHead>
                <TableHead className="min-w-[200px]">Email</TableHead>
                <TableHead className="w-28">Federal/State</TableHead>
                <TableHead className="w-24">Chamber</TableHead>
                <TableHead className="min-w-[150px]">Electorate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(r=> (
                <TableRow key={r.id}>
                  <TableCell className="w-12"><input type="checkbox" checked={!!sel[r.id]} onChange={()=>toggle(r.id)} /></TableCell>
                  <TableCell className="min-w-[180px]">{r.name || r.display_name || `${r.first_name||''} ${r.last_name||''}`.trim()}</TableCell>
                  <TableCell className="w-16">{r.state}</TableCell>
                  <TableCell className="min-w-[200px] text-sm">{r.email}</TableCell>
                  <TableCell className="w-28">{r.level ? r.level.charAt(0).toUpperCase() + r.level.slice(1) : ''}</TableCell>
                  <TableCell className="w-24">{r.house ? r.house.charAt(0).toUpperCase() + r.house.slice(1) : ''}</TableCell>
                  <TableCell className="min-w-[150px]">{r.electorate || r.region || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end gap-2"><Button onClick={apply} data-testid="rp-apply">Apply</Button></div>
      </div>
    </div>
  );
}

// Collapsible Section Component
export function CollapsibleSection({ title, isOpen, onToggle, children }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-sm transition-colors"
      >
        <span>{title}</span>
        <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {isOpen && (
        <div className="p-4 space-y-3 border-t">
          {children}
        </div>
      )}
    </div>
  );
}

// Render subtitle with bold tags and newlines
function renderSubtitle(text) {
  if (!text) return null;
  // Split by newlines first, then handle bold tags
  const lines = text.split('\n');
  return lines.map((line, lineIdx) => {
    // Handle **bold** and <b>bold</b> tags
    const parts = line.split(/(\*\*[^*]+\*\*|<b>[^<]+<\/b>)/g);
    const rendered = parts.map((part, partIdx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={partIdx}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('<b>') && part.endsWith('</b>')) {
        return <strong key={partIdx}>{part.slice(3, -4)}</strong>;
      }
      return part;
    });
    return (
      <span key={lineIdx}>
        {rendered}
        {lineIdx < lines.length - 1 && <br />}
      </span>
    );
  });
}

export function WidgetPreview({ title, subtitle, showSubtitle, theme = {}, requireEmail = false, requireName = false, personalise = true, showPersonalisation, dataCollection = false, recipients = "", inputPlaceholders, campaignType = "set_and_send", signSendConfig = {}, submissionConfig = {}, socialSurgeConfig = {}, buttonStyle = {}, onPatch, onOpenDrawer, onButtonStyleChange, onConfigPatch }){
  const [showPreviewTerms, setShowPreviewTerms] = useState(false);
  const [previewConsent, setPreviewConsent] = useState(false);
  const previewConsentId = `preview-${React.useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const personalisationShown = personalisationVisible(showPersonalisation, personalise);
  // Inline-edit callback. `onPatch('field_name', value)` mutates the parent form.
  const patch = typeof onPatch === 'function' ? onPatch : () => {};
  // Nested config patcher: onConfigPatch('sign_send_config', 'counter_count_label', 'have joined!')
  const cfgPatch = typeof onConfigPatch === 'function' ? onConfigPatch : () => {};
  // Drawer opener. Accepts a panel id: 'logo' | 'title' | 'fields' | 'button' | 'bg'.
  const openDrawer = typeof onOpenDrawer === 'function' ? onOpenDrawer : () => {};
  const rawOrg = (getOrgInfo() || { name: 'Organisation', logo_url: '', privacy_policy_url: '' });
  
  // Ensure logo URL is absolute
  let logoUrl = rawOrg.logo_url || '';
  if (logoUrl && logoUrl.startsWith('/')) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    logoUrl = backendUrl + logoUrl;
  }
  const org = { ...rawOrg, logo_url: logoUrl };
  
  const t = {
    ...(theme || {}),
    show_logo: theme.show_logo !== false,
    show_title: theme.show_title !== false,
    font: theme.font || 'Inter',
    font_title: theme.font_title || theme.font || 'Inter',
    primary: theme.primary || '#0a2a66',
    primaryText: theme.primaryText || '#ffffff',
    secondary: theme.secondary || '#eef2ff',
    fieldText: theme.fieldText || '#0a0a0a',
    fieldBorder: theme.fieldBorder || '#d1d5db',
    titleColor: theme.titleColor || theme.text || '#0a0a0a',
    bg: theme.bg || '#ffffff',
    text: theme.text || '#000000',
    scrollbarColor: theme.scrollbarColor || theme.primary || '#0a2a66',
    buttonColor: theme.buttonColor || theme.secondary || '#eef2ff'
  };
  
  // Ensure font values are never empty strings
  const safeFont = t.font || 'Inter';
  const safeFontTitle = t.font_title || t.font || 'Inter';
  const fontFamily = fontStack(safeFont);
  const fontFamilyTitle = fontStack(safeFontTitle);

  // ─── New theme keys applied here (Phase 2) ────────────────────────────────
  // Map textual sizes → tailwind classes. NOTE: these class strings must be
  // referenced literally somewhere in the source for Tailwind's purge to keep
  // them. Listed here as comments + a safelist string below for the JIT.
  // safelist: text-lg text-xl text-2xl text-3xl text-4xl text-xs text-sm text-base
  // safelist: text-left text-center text-right items-start items-center items-end
  // safelist: justify-start justify-center justify-end
  const TITLE_SIZE = { lg: 'text-lg', xl: 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl' };
  const SUBTITLE_SIZE = { xs: 'text-xs', sm: 'text-sm', base: 'text-base', lg: 'text-lg' };
  const ALIGN_CLASS = { left: 'text-left items-start', center: 'text-center items-center', right: 'text-right items-end' };
  const FLEX_ALIGN = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };

  const logoAlign = t.logoAlign || 'center';
  const logoSize  = Math.max(16, Math.min(160, Number(t.logoSize) || 40));
  const logoShadow = !!t.logoShadow;
  const titleSizeCls    = TITLE_SIZE[t.titleSize || '2xl'] || 'text-2xl';
  const titleAlignCls   = ALIGN_CLASS[t.titleAlign || 'center'] || ALIGN_CLASS.center;
  const subtitleSizeCls = SUBTITLE_SIZE[t.subtitleSize || 'sm'] || 'text-sm';
  const subtitleAlignCls= ALIGN_CLASS[t.subtitleAlign || t.titleAlign || 'center'] || ALIGN_CLASS.center;
  const titleStyle = {
    fontFamily: fontFamilyTitle,
    color: t.titleColor,
    ...(t.titleShadow ? { textShadow: '0 2px 8px rgba(0,0,0,0.25)' } : {}),
    ...(t.titleBg ? { background: t.titleBg, padding: '4px 12px', borderRadius: 8, display: 'inline-block' } : {}),
  };
  const subtitleStyle = {
    fontFamily: fontFamily,
    color: t.subtitleColor || t.text,
  };
  // Background image layer styles (applied inside the card via ::before-like wrapping)
  const hasBgImage = !!t.bgImage;
  const bgBlur = Math.max(0, Math.min(30, Number(t.bgBlur) || 0));
  const bgOpacity = Math.max(0, Math.min(1, t.bgImageOpacity == null ? 1 : Number(t.bgImageOpacity)));
  // NOTE: no `overflow: hidden` here — that would clip the floating Edit pencils
  // that sit just outside elements. The bg image gets its own clipped wrapper below.
  const cardOuterStyle = { background: t.bg, color: t.text, fontFamily: fontFamily, position: 'relative' };
  const bgLayer = hasBgImage ? (
    <div aria-hidden className="absolute inset-0 z-0 pointer-events-none rounded-lg overflow-hidden">
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(${t.bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: bgBlur > 0 ? `blur(${bgBlur}px)` : 'none',
        opacity: bgOpacity,
      }} />
    </div>
  ) : null;
  
  useEffect(()=>{ ensureGoogleFontLoaded(safeFont); ensureGoogleFontLoaded(safeFontTitle); }, [safeFont, safeFontTitle]);
  
  
  // For Sign & Send with set_recipients mode, use those recipients; otherwise use the recipients prop
  const effectiveRecipients = (campaignType === "sign_and_send" && signSendConfig?.recipient_mode === "set_recipients") 
    ? (signSendConfig?.set_recipients || "") 
    : recipients;
  const bccList = effectiveRecipients.split(/[;,]/).map(s=>s.trim()).filter(Boolean);
  
  const subj = (title || 'Regarding constituent concerns').slice(0,80);
  const body = "Dear Representative,\n\nShort email body preview...\n\nThank you\n";
  const ph = inputPlaceholders || {};
  const mockSignatureCount = 1234;
  const mockSignatureGoal = signSendConfig?.signature_goal || 1000;
  const displayMode = signSendConfig?.counter_display_mode || "count_only";
  
  // For Submission Builder preview - include preamble address at top of body
  const preambleAddress = submissionConfig?.preamble_address || "";
  const mockSubmissionBody = submissionConfig?.inquiry_name 
    ? `${preambleAddress ? preambleAddress + '\n\n' : ''}Dear Committee Members,\n\nI am writing to provide my submission regarding the ${submissionConfig.inquiry_name}.\n\n[AI-generated submission content would appear here based on your description and user's personalisation...]\n\nSincerely,\nJane Doe\njane.doe@email.com\n0400 000 000`
    : `${preambleAddress ? preambleAddress + '\n\n' : ''}Dear Committee Members,\n\n[Submission content...]\n\nSincerely,\nJane Doe\njane.doe@email.com\n0400 000 000`;
  
  // Signature counter component for reuse
  const counterProgressTpl = signSendConfig?.counter_progress_label || 'of {goal} signatures';
  const counterCountTpl = signSendConfig?.counter_count_label || 'people have signed';
  const renderTpl = (tpl) => tpl.replace('{count}', mockSignatureCount.toLocaleString()).replace('{goal}', mockSignatureGoal.toLocaleString());
  const SignatureCounter = () => (
    <div className="text-center py-2 px-4">
      <div className="text-3xl font-bold" style={{ color: t.primary }}>{mockSignatureCount.toLocaleString()}</div>
      {displayMode === "progress" ? (
        <>
          <div className="text-sm opacity-75 relative group inline-block" style={{ color: t.text }}>
            <EditableText
              value={counterProgressTpl}
              placeholder="of {goal} signatures"
              onSave={(v) => cfgPatch('sign_send_config', 'counter_progress_label', v)}
              testId="editable-counter-progress"
              ariaLabel="Counter progress label"
            />
            <div className="text-[10px] opacity-60 mt-0.5">Tip: use {'{'}count{'}'} and {'{'}goal{'}'} placeholders. Preview: {renderTpl(counterProgressTpl)}</div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className="h-2 rounded-full" 
              style={{ 
                backgroundColor: t.primary, 
                width: `${Math.min(100, (mockSignatureCount / mockSignatureGoal) * 100)}%` 
              }}
            ></div>
          </div>
        </>
      ) : (
        <div className="text-sm opacity-75" style={{ color: t.text }}>
          <EditableText
            value={counterCountTpl}
            placeholder="people have signed"
            onSave={(v) => cfgPatch('sign_send_config', 'counter_count_label', v)}
            testId="editable-counter-count"
            ariaLabel="Counter signed label"
          />
        </div>
      )}
    </div>
  );
  
  return (
    <div className="w-full space-y-4 widget-preview-scope">
      <TermsDialog open={showPreviewTerms} onOpenChange={setShowPreviewTerms} organisation={org.name} />
      <style>{`
        .widget-preview-scope input::placeholder, .widget-preview-scope textarea::placeholder { color: ${t.fieldText}; opacity: 0.75; }
        .widget-preview-scope input, .widget-preview-scope textarea { background-color: ${t.fieldInterior || '#ffffff'} !important; color: ${t.fieldText} !important; border-color: ${t.fieldBorder} !important; box-shadow: none !important; opacity: 1 !important; }
      `}</style>
      
      {/* Signature Counter (top position for Sign & Send) */}
      {campaignType === "sign_and_send" && signSendConfig.show_counter && signSendConfig.counter_position === "top" && (
        <div className="rounded" style={{ backgroundColor: t.secondary }}>
          <SignatureCounter />
        </div>
      )}
      
      {/* Compose step preview */}
      <Card className="w-full shadow border relative group" data-testid="widget-preview-compose" style={cardOuterStyle}>
        <EditAffordance panel="bg" onOpen={openDrawer} label="Edit background" position="top-2 right-2" />
        {bgLayer}
        <CardHeader className="title-font relative z-10" style={{ fontFamily: fontFamilyTitle }}>
          <div className={`flex flex-col gap-3 ${ALIGN_CLASS[logoAlign].split(' ')[1]}`}>
            {t.show_logo !== false && org?.logo_url && (
              <div className={`flex ${FLEX_ALIGN[logoAlign]} w-full`}>
                <div className="relative group inline-block">
                  <img
                    src={org.logo_url}
                    alt="logo"
                    data-testid="preview-logo"
                    style={{ height: `${logoSize}px`, width: 'auto', filter: logoShadow ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))' : 'none', display: 'block' }}
                  />
                  <EditAffordance panel="logo" onOpen={openDrawer} label="Edit logo" />
                </div>
              </div>
            )}
            {t.show_title !== false && (
              <div className={`w-full flex flex-col ${ALIGN_CLASS[t.titleAlign || 'center'].split(' ')[1]}`}>
                <div className="relative group inline-block max-w-full">
                  <CardTitle className={`${titleSizeCls} ${ALIGN_CLASS[t.titleAlign || 'center'].split(' ')[0]}`} data-testid="preview-title" style={{ fontFamily: fontFamilyTitle, color: t.titleColor }}>
                    <span style={titleStyle}>
                      <EditableText
                        value={title}
                        placeholder="Campaign title"
                        onSave={(v) => patch('title', v)}
                        style={{ fontFamily: fontFamilyTitle, color: t.titleColor }}
                        testId="editable-title"
                        ariaLabel="Campaign title"
                      />
                    </span>
                  </CardTitle>
                  <EditAffordance panel="title" onOpen={openDrawer} label="Edit title" />
                </div>
                {showSubtitle && (
                  <div className={`${subtitleSizeCls} ${subtitleAlignCls.split(' ')[0]} mt-2 opacity-80 w-full`} style={subtitleStyle}>
                    <EditableText
                      value={subtitle}
                      placeholder="Click to add subtitle…"
                      onSave={(v) => patch('subtitle', v)}
                      style={subtitleStyle}
                      multiline
                      testId="editable-subtitle"
                      ariaLabel="Campaign subtitle"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4 relative z-10">
          <div className="relative group space-y-3">
            <EditAffordance panel="fields" onOpen={openDrawer} label="Edit fields" position="top-1 right-1" />
          <div className="space-y-2">
            {/* MP Ping postcode field */}
            {campaignType === "mp_ping" && (
              <div className="space-y-2">
                <div className="text-sm font-medium" style={{ color: t.text }}>Find your electorate</div>
                <Input disabled placeholder="e.g. 2000 / Perth / Anthony Albanese" style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />
              </div>
            )}
            
            {/* Sign & Send postcode field (when recipient_mode is postcode) */}
            {campaignType === "sign_and_send" && signSendConfig?.recipient_mode === "postcode" && (
              <div className="space-y-2">
                <div className="text-sm font-medium" style={{ color: t.text }}>Find your electorate</div>
                <Input disabled placeholder="e.g. 2000 / Perth / Anthony Albanese" style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />
              </div>
            )}
            
            {/* Submission Builder: Name, Phone, Email (all required) */}
            {campaignType === "submission_builder" && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input disabled placeholder={`${ph.name || "Your full name"} *`} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />
                  <Input disabled placeholder={`${ph.phone || "Your phone"} *`} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />
                </div>
                <Input disabled placeholder={`${ph.email || "Your email"} *`} style={{ background: t.secondary, color: t.fieldText }} />
                {submissionConfig?.require_address && (
                  <Input disabled placeholder="Your full address *" style={{ background: t.secondary, color: t.fieldText }} />
                )}
                <div className="space-y-2 mt-2">
                  <div className="text-xs font-medium" style={{ color: t.text }}>Submission Type</div>
                  <div className="space-y-1">
                    <label className="flex items-start gap-2 text-xs p-2 rounded" style={{ backgroundColor: t.secondary, color: t.text }}>
                      <input type="radio" name="preview_sub_type" checked readOnly className="mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">
                          <EditableText
                            value={submissionConfig?.type_public_label || 'Public'}
                            placeholder="Public"
                            onSave={(v) => cfgPatch('submission_config', 'type_public_label', v)}
                            testId="editable-sub-public-label"
                            ariaLabel="Public submission type label"
                          />
                        </div>
                        <div className="opacity-70">
                          <EditableText
                            value={submissionConfig?.type_public_desc || 'Your name and submission will be published on the parliament website'}
                            placeholder="Description"
                            onSave={(v) => cfgPatch('submission_config', 'type_public_desc', v)}
                            multiline
                            testId="editable-sub-public-desc"
                            ariaLabel="Public submission type description"
                          />
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 text-xs p-2 rounded hover:bg-gray-50" style={{ color: t.text }}>
                      <input type="radio" name="preview_sub_type" readOnly className="mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">
                          <EditableText
                            value={submissionConfig?.type_withheld_label || 'Name Withheld'}
                            placeholder="Name Withheld"
                            onSave={(v) => cfgPatch('submission_config', 'type_withheld_label', v)}
                            testId="editable-sub-withheld-label"
                            ariaLabel="Name withheld type label"
                          />
                        </div>
                        <div className="opacity-70">
                          <EditableText
                            value={submissionConfig?.type_withheld_desc || 'Your submission will be published, but your name will not appear'}
                            placeholder="Description"
                            onSave={(v) => cfgPatch('submission_config', 'type_withheld_desc', v)}
                            multiline
                            testId="editable-sub-withheld-desc"
                            ariaLabel="Name withheld type description"
                          />
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 text-xs p-2 rounded hover:bg-gray-50" style={{ color: t.text }}>
                      <input type="radio" name="preview_sub_type" readOnly className="mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">
                          <EditableText
                            value={submissionConfig?.type_private_label || 'Private'}
                            placeholder="Private"
                            onSave={(v) => cfgPatch('submission_config', 'type_private_label', v)}
                            testId="editable-sub-private-label"
                            ariaLabel="Private submission type label"
                          />
                        </div>
                        <div className="opacity-70">
                          <EditableText
                            value={submissionConfig?.type_private_desc || 'Only the committee will see your submission - it will not be published'}
                            placeholder="Description"
                            onSave={(v) => cfgPatch('submission_config', 'type_private_desc', v)}
                            multiline
                            testId="editable-sub-private-desc"
                            ariaLabel="Private submission type description"
                          />
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            {/* Sign & Send: Name and Email on same row */}
            {campaignType === "sign_and_send" ? (
              <div className="flex gap-2">
                <Input disabled placeholder={(ph.name || "Your name").substring(0, 40)} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />
                <Input disabled placeholder={`${(ph.email || "Your email").substring(0, 40)} *`} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />
              </div>
            ) : campaignType !== "submission_builder" && campaignType !== "social_surge" && (requireName || requireEmail) && (
              <div className="flex gap-2">
                {requireName && (<Input disabled placeholder={(ph.name || "Your name").substring(0, 40)} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />)}
                {requireEmail && (<Input disabled placeholder={(ph.email || "Your email").substring(0, 40)} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />)}
              </div>
            )}
            
            {/* Personalisation - Not shown for Social Surge (has its own), and controllable via showPersonalisation */}
            {personalisationShown && campaignType !== "social_surge" && (<Textarea data-testid="preview-personalisation" disabled placeholder={(ph.personalisation || "Optional personalisation").substring(0, 80)} style={{ background: t.secondary, color: t.fieldText }} className="text-sm" />)}
          </div>
          
          {/* T&Cs: Show for Sign & Send, Submission Builder, OR when consent needed - but NOT for Social Surge (handled separately) */}
          {campaignType !== "social_surge" && (
            <div data-testid="preview-consent" style={{ color: t.text, fontFamily: fontFamily }}>
              <ParticipationConsent prefix={previewConsentId} checked={previewConsent} onChange={setPreviewConsent} onTerms={() => setShowPreviewTerms(true)} />
            </div>
          )}
          
          {/* Social Surge preview - different UI */}
          {campaignType === "social_surge" ? (
            <div className="space-y-3">
              {/* Name/Email fields for Social Surge - on same line */}
              {(requireName || requireEmail) && (
                <div className="flex gap-2">
                  {requireName && (<Input disabled placeholder={(ph.name || "Your name").substring(0, 40)} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />)}
                  {requireEmail && (<Input disabled placeholder={(ph.email || "Your email").substring(0, 40)} style={{ background: t.secondary, color: t.fieldText }} className="flex-1" />)}
                </div>
              )}
              {personalisationShown && (<Textarea data-testid="preview-personalisation" disabled placeholder={(ph.personalisation || "Optional personalisation").substring(0, 80)} style={{ background: t.secondary, color: t.fieldText }} className="text-sm" />)}
              <div data-testid="preview-consent" style={{ color: t.text, fontFamily: fontFamily }}>
                <ParticipationConsent prefix={previewConsentId} checked={previewConsent} onChange={setPreviewConsent} onTerms={() => setShowPreviewTerms(true)} />
              </div>
              <div className={buttonStyle?.alignment === 'right' ? 'text-right' : buttonStyle?.alignment === 'left' ? 'text-left' : 'text-center'}>
                <Button disabled style={{ backgroundColor: t.primary, color: t.primaryText, opacity: 1, fontFamily,
                  borderRadius: buttonStyle.radius != null ? `${buttonStyle.radius}px` : (buttonStyle.rounded === 'full' ? '9999px' : buttonStyle.rounded === 'none' ? '0' : '6px'),
                  padding: buttonStyle.size === 'sm' ? '6px 12px' : buttonStyle.size === 'lg' ? '14px 28px' : '10px 20px',
                  width: buttonStyle.width === 'full' || buttonStyle.alignment === 'full' ? '100%' : buttonStyle.width === 'half' ? '50%' : 'auto'
                }} data-testid="preview-generate">
                  {buttonStyle.label || "Generate Posts"}
                </Button>
              </div>
              <div className="text-xs opacity-70">We will generate a custom AI-written post</div>
            </div>
          ) : (
          <div className={`space-y-1 ${buttonStyle?.alignment === 'left' ? 'text-left' : buttonStyle?.alignment === 'right' ? 'text-right' : buttonStyle?.alignment === 'full' ? '' : 'text-center'}`}>
            <div className={`relative group ${(buttonStyle?.width === 'full' || buttonStyle?.alignment === 'full') ? 'block' : 'inline-block'}`}>
            <Button 
              disabled 
              style={{ 
                backgroundColor: t.primary, 
                color: t.primaryText, 
                opacity: 1, 
                fontFamily: fontFamily,
                borderRadius: (buttonStyle?.radius != null ? `${buttonStyle.radius}px` : (buttonStyle?.rounded === 'none' ? '0' : buttonStyle?.rounded === 'sm' ? '4px' : buttonStyle?.rounded === 'lg' ? '12px' : buttonStyle?.rounded === 'full' ? '9999px' : '6px')),
                padding: buttonStyle?.size === 'sm' ? '6px 12px' : buttonStyle?.size === 'lg' ? '14px 28px' : '10px 20px',
                fontSize: buttonStyle?.size === 'sm' ? '12px' : buttonStyle?.size === 'lg' ? '16px' : '14px',
                width: (buttonStyle?.width === 'full' || buttonStyle?.alignment === 'full') ? '100%' : (buttonStyle?.width === 'half' ? '50%' : 'auto')
              }} 
              data-testid="preview-generate"
            >
              <EditableText
                value={buttonStyle?.label}
                placeholder={campaignType === "sign_and_send" ? "Sign & Send" : campaignType === "submission_builder" ? "Generate Submission" : "Generate"}
                onSave={(v) => onButtonStyleChange ? onButtonStyleChange({ ...(buttonStyle || {}), label: v }) : null}
                style={{ color: t.primaryText, fontFamily: fontFamily }}
                testId="editable-button-label"
                ariaLabel="Button label"
              />
            </Button>
            <EditAffordance panel="button" onOpen={openDrawer} label="Edit button" />
            </div>
            {campaignType === "sign_and_send" && (
              <div className="text-xs text-center opacity-75" style={{ color: t.text }}>
                <EditableText
                  value={signSendConfig?.button_caption || "By signing, you'll add your name to the petition and send an AI-written email"}
                  placeholder="Caption shown under the button"
                  onSave={(v) => cfgPatch('sign_send_config', 'button_caption', v)}
                  multiline
                  testId="editable-signsend-caption"
                  ariaLabel="Sign & Send caption"
                />
              </div>
            )}
            {campaignType === "submission_builder" && (
              <div className="text-xs" style={{ color: t.text }}>
                <EditableText
                  value={submissionConfig?.disclosure_text || `Your submission will include your name, email${submissionConfig?.require_address ? ", phone number and address" : " and phone number"}. NOTE: Incomplete information will likely lead to submissions being rejected.`}
                  placeholder="Disclosure text shown under the Generate button"
                  onSave={(v) => cfgPatch('submission_config', 'disclosure_text', v)}
                  multiline
                  testId="editable-submission-disclosure"
                  ariaLabel="Submission disclosure"
                />
              </div>
            )}
          </div>
          )}
          <div className="text-xs opacity-70">Compose preview</div>
          </div>{/* /relative group fields-wrapper */}
        </CardContent>
        
        {/* Signature Counter (bottom position for Sign & Send) */}
        {campaignType === "sign_and_send" && signSendConfig.show_counter && signSendConfig.counter_position === "bottom" && (
          <div className="border-t" style={{ backgroundColor: t.secondary }}>
            <SignatureCounter />
          </div>
        )}
      </Card>

      {/* Send step preview - NOT shown for Social Surge */}
      {campaignType !== "social_surge" && (
      <Card className="w-full shadow border" data-testid="widget-preview-send" style={{ background: t.bg, color: t.text, fontFamily: fontFamily }}>
        <style>{`
          .send-preview-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
          .send-preview-scroll::-webkit-scrollbar-track { background: ${t.bg}; border-radius: 4px; }
          .send-preview-scroll::-webkit-scrollbar-thumb { background: ${t.scrollbarColor}; border-radius: 4px; }
        `}</style>
        <CardHeader className="title-font" style={{ fontFamily: fontFamilyTitle }}>
          <div className="flex flex-col items-center gap-3">
            {org?.logo_url && t.show_logo && (<img src={org.logo_url} alt="logo" className="h-10" />)}
            {t.show_title && (<CardTitle className="text-2xl text-center" style={{ fontFamily: fontFamilyTitle, color: t.titleColor }}>{title || 'Campaign title'}</CardTitle>)}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button disabled className="w-full" style={{ backgroundColor: t.primary, color: t.primaryText, opacity: 1 }} data-testid="preview-send">
            {campaignType === "submission_builder" ? "Click Here to Send Submission" : "Click Here to Send Now"}
          </Button>
          <div className="space-y-2 send-preview-scroll overflow-y-auto" style={{ maxHeight: '350px' }}>
            <div className="text-sm font-medium">
              {campaignType === "submission_builder" 
                ? "Button not working? Copy/paste this into a new email to the committee:" 
                : "Button not working? Copy/paste this into a new email:"}
            </div>
            <div className="space-y-1 border rounded p-3" style={{ background: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{campaignType === "submission_builder" ? "Committee Email(s)" : "BCC"}</div>
                <Button size="sm" variant="secondary" disabled style={{ backgroundColor: t.buttonColor, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, opacity: 1 }}>Copy</Button>
              </div>
              <Textarea readOnly value={campaignType === "submission_builder" ? (submissionConfig?.committee_emails || "committee@parliament.gov.au") : bccList.join('; ')} data-testid="preview-bcc" style={{ backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }} />
            </div>
            <div className="space-y-1 border rounded p-3" style={{ background: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Subject</div>
                <Button size="sm" variant="secondary" disabled style={{ backgroundColor: t.buttonColor, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, opacity: 1 }}>Copy</Button>
              </div>
              <Input readOnly value={campaignType === "submission_builder" ? `Public Submission: ${submissionConfig?.inquiry_name || title || 'Inquiry'} - Jane Doe` : subj} data-testid="preview-subject" style={{ backgroundColor: t.secondary, color: t.fieldText, borderColor: t.fieldBorder }} />
            </div>
            <div className="space-y-1 border rounded p-3" style={{ background: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Body</div>
                <Button size="sm" variant="secondary" disabled style={{ backgroundColor: t.buttonColor, color: t.fieldText, border: `1px solid ${t.fieldBorder}`, opacity: 1 }}>Copy</Button>
              </div>
              <Textarea readOnly rows={8} value={campaignType === "submission_builder" ? mockSubmissionBody : body} data-testid="preview-body" style={{ backgroundColor: t.secondary, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }} />
            </div>
          </div>
          <div className="text-xs opacity-70">Send preview</div>
        </CardContent>
      </Card>
      )}
      
      {/* Social Surge Share Step Preview */}
      {campaignType === "social_surge" && (
      <Card className="w-full shadow border" data-testid="widget-preview-share" style={{ background: t.bg, color: t.text, fontFamily: fontFamily }}>
        <CardHeader className="title-font" style={{ fontFamily: fontFamilyTitle }}>
          <div className="flex flex-col items-center gap-3">
            {org?.logo_url && t.show_logo && (<img src={org.logo_url} alt="logo" className="h-10" />)}
            {t.show_title && (<CardTitle className="text-2xl text-center" style={{ fontFamily: fontFamilyTitle, color: t.titleColor }}>{title || 'Campaign title'}</CardTitle>)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Facebook Share Preview */}
          {socialSurgeConfig?.platforms?.includes("facebook") && (
            <div className="space-y-3 p-4 rounded-lg border" style={{ backgroundColor: t.secondary, borderColor: t.fieldBorder }}>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="font-semibold" style={{ color: t.text }}>Share on Facebook</span>
              </div>
              
              <div className="text-sm" style={{ color: t.text }}>
                <span className="font-medium">Step 1:</span> Copy this text
              </div>
              <Button disabled className="w-full" style={{ backgroundColor: '#1877F2', color: '#fff', opacity: 0.7 }}>
                Copy Text
              </Button>
              <Textarea 
                readOnly
                value="[AI-generated Facebook post will appear here. Users can edit before sharing.]"
                rows={3}
                style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}
              />
              
              <div className="text-sm" style={{ color: t.text }}>
                <span className="font-medium">Step 2:</span> Click below to open Facebook, then paste the text
              </div>
              <Button disabled className="w-full" style={{ backgroundColor: '#1877F2', color: '#fff', opacity: 0.7 }}>
                Open Facebook
              </Button>
            </div>
          )}
          
          {/* X Share Preview */}
          {socialSurgeConfig?.platforms?.includes("x") && (
            <div className="space-y-3 p-4 rounded-lg border" style={{ backgroundColor: t.secondary, borderColor: t.fieldBorder }}>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="font-semibold" style={{ color: t.text }}>Share on X</span>
              </div>
              
              <div className="text-sm" style={{ color: t.text }}>
                Review your {socialSurgeConfig?.x_post_type === "quote_tweet" ? "quote tweet" : socialSurgeConfig?.x_post_type === "reply" ? "reply" : "post"}, then click below to share:
              </div>
              <div className="relative">
                <Textarea 
                  readOnly
                  value="[AI-generated tweet will appear here. Users can edit before sharing.]"
                  rows={2}
                  style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}
                />
                <div className="text-xs text-right mt-1" style={{ color: t.fieldText }}>
                  0/280
                </div>
              </div>
              {socialSurgeConfig?.x_quote_tweet_url && (socialSurgeConfig?.x_post_type === "quote_tweet" || socialSurgeConfig?.x_post_type === "reply") && (
                <div className="text-xs p-2 rounded" style={{ backgroundColor: t.bg, color: t.fieldText, border: `1px solid ${t.fieldBorder}` }}>
                  <span className="font-medium">{socialSurgeConfig?.x_post_type === "quote_tweet" ? "Quoting:" : "Replying to:"}</span> {socialSurgeConfig.x_quote_tweet_url}
                </div>
              )}
              <Button disabled className="w-full" style={{ backgroundColor: '#000', color: '#fff', opacity: 0.7 }}>
                {socialSurgeConfig?.x_post_type === "quote_tweet" ? "Quote Tweet on X" : socialSurgeConfig?.x_post_type === "reply" ? "Reply on X" : "Post on X"}
              </Button>
            </div>
          )}
          
          <Button variant="ghost" disabled className="text-sm">
            ← Generate new posts
          </Button>
          <div className="text-xs opacity-70">Share preview</div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}

function EmbedCodeModal({ open, id, defaultBg = '#ffffff', onClose }){
  if(!open) return null;
  const [bg,setBg]=useState(defaultBg);
  const [width,setWidth]=useState('100%');
  const [maxWidth,setMaxWidth]=useState('600px');
  const [height,setHeight]=useState('700px');
  const [padding,setPadding]=useState('12px');
  const [hAlign,setHAlign]=useState('center'); // left, center, right
  const [vAlign,setVAlign]=useState('top'); // top, middle, bottom
  
  const base = typeof window!=='undefined'? window.location.origin: '';
  // Pass padding to widget URL so it controls spacing around the card
  const src = `${base}/widget/${id}?outerBackground=${encodeURIComponent(bg)}&padding=${encodeURIComponent(padding)}`;
  
  // Generate alignment styles for the container
  const getContainerStyle = () => {
    const styles = [`background:${bg}`];
    styles.push('display:flex');
    if (hAlign === 'left') styles.push('justify-content:flex-start');
    else if (hAlign === 'right') styles.push('justify-content:flex-end');
    else styles.push('justify-content:center');
    if (vAlign === 'top') styles.push('align-items:flex-start');
    else if (vAlign === 'bottom') styles.push('align-items:flex-end');
    else styles.push('align-items:center');
    styles.push(`padding:${padding}`);
    return styles.join(';');
  };
  
  const containerStyle = getContainerStyle();
  const code = `<div style="${containerStyle}">
  <iframe src="${src}" style="width:${width};${maxWidth !== 'none' ? `max-width:${maxWidth};` : ''}height:${height};border:0;border-radius:8px;box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
</div>`;

  const handleCopy = ()=>{
    const ta = document.createElement('textarea');
    ta.value = code; 
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta); 
    ta.focus();
    ta.select(); 
    try {
      document.execCommand('copy');
      toast.success('Embed code copied!');
    } catch(err) {
      navigator.clipboard?.writeText(code).then(
        () => toast.success('Embed code copied!'),
        () => toast.error('Copy failed. Please select and copy manually.')
      );
    }
    document.body.removeChild(ta);
  };
  
  // Preview alignment classes
  const previewHClass = hAlign === 'left' ? 'justify-start' : hAlign === 'right' ? 'justify-end' : 'justify-center';
  const previewVClass = vAlign === 'top' ? 'items-start' : vAlign === 'bottom' ? 'items-end' : 'items-center';
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="embed-modal" onClick={(e)=>e.target===e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-white font-semibold text-lg">Embed Widget</h2>
            <p className="text-blue-100 text-sm">Add this widget to your website</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1" type="button">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Settings */}
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">iframe Size</h3>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Width</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={width} onChange={(e)=>setWidth(e.target.value)} placeholder="100%" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Max Width</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={maxWidth} onChange={(e)=>setMaxWidth(e.target.value)} placeholder="600px" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Height</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={height} onChange={(e)=>setHeight(e.target.value)} placeholder="700px" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Background</h3>
                <div className="flex items-center gap-2">
                  <input type="color" value={bg} onChange={(e)=>setBg(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-200" />
                  <input className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono bg-gray-50" value={bg} onChange={(e)=>setBg(e.target.value)} placeholder="#ffffff" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Padding</h3>
                <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" value={padding} onChange={(e)=>setPadding(e.target.value)} placeholder="12px" />
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Position</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Horizontal</span>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                    {[['left','Left'],['center','Center'],['right','Right']].map(([val,label])=>(
                      <button type="button" key={val} onClick={()=>setHAlign(val)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${hAlign===val?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Vertical</span>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                    {[['top','Top'],['middle','Middle'],['bottom','Bottom']].map(([val,label])=>(
                      <button type="button" key={val} onClick={()=>setVAlign(val)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${vAlign===val?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Embed Code</h3>
              <textarea readOnly value={code} className="w-full h-28 border rounded-lg p-3 font-mono text-xs bg-gray-900 text-green-400 resize-none" />
            </div>
          </div>
          
          {/* Right: Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
            <div className="border-2 border-dashed border-gray-200 rounded-xl overflow-hidden bg-gray-50 p-2">
              <div className="text-xs text-gray-400 mb-1 text-center">Your webpage</div>
              {/* The iframe container with background color and padding */}
              <div 
                className={`flex ${previewHClass} ${previewVClass} rounded-lg border shadow-inner overflow-hidden transition-all duration-300`}
                style={{ height: '320px', backgroundColor: bg, padding: padding }}
              >
                {/* The widget card (centered, with max-width like real widget) */}
                <div 
                  className="bg-white rounded-lg shadow-lg flex flex-col overflow-hidden"
                  style={{ width: '160px', minHeight: '280px' }}
                >
                  {/* Mock widget header */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-2 flex-shrink-0 rounded-t-lg">
                    <div className="w-16 h-2 bg-white/40 rounded mb-1"></div>
                    <div className="w-10 h-1.5 bg-white/25 rounded"></div>
                  </div>
                  {/* Mock widget body */}
                  <div className="p-2 space-y-1.5 bg-white flex-1 rounded-b-lg">
                    <div className="w-full h-5 bg-gray-100 rounded"></div>
                    <div className="w-full h-5 bg-gray-100 rounded"></div>
                    <div className="w-full h-8 bg-gray-100 rounded"></div>
                    <div className="w-full h-5 bg-blue-500 rounded mt-2"></div>
                  </div>
                </div>
              </div>
              <div className="text-center mt-2">
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">
                  Position: {hAlign} / {vAlign}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t sticky bottom-0">
          <div className="text-xs text-gray-500">
            Paste this code where you want the widget to appear.
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition">
              Close
            </button>
            <button type="button" onClick={handleCopy} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition shadow-sm">
              Copy Embed Code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const tokenHasImp = () => {
  try { const t=localStorage.getItem('token'); if(!t) return false; return !!JSON.parse(atob(t.split('.')[1]||''))?.imp; } catch { return false; }
};

function UpperHouseSelectionModal({ members, loading, config, onToggle, onSelectAll, onClearAll, onSelectAllParty, onClose }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedParty, setSelectedParty] = useState("all");
  
  const membersByState = {};
  members.forEach(m => {
    if (!membersByState[m.state]) membersByState[m.state] = [];
    membersByState[m.state].push(m);
  });
  
  const states = Object.keys(membersByState).sort();
  const selectedIds = config.selected_senators || {};
  
  // Get unique parties
  const allParties = [...new Set(members.map(m => m.party).filter(Boolean))].sort();
  
  // Map party codes to full names
  const partyNames = {
    'ALP': 'Labor',
    'LP': 'Liberal',
    'NAT': 'Nationals',
    'NATS': 'Nationals',
    'LNP': 'LNP',
    'GRN': 'Greens',
    'GRNS': 'Greens',
    'AG': 'Greens',
    'IND': 'Independent',
    'ON': 'One Nation',
    'ONP': 'One Nation',
    'PHON': 'One Nation',
    'UAP': 'UAP',
    'JLN': 'JLN',
    'AV': 'AV Party'
  };
  
  // Filter members based on search term and party
  const filteredMembersByState = {};
  states.forEach(state => {
    const filtered = membersByState[state].filter(m => {
      const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesParty = selectedParty === "all" || m.party === selectedParty;
      return matchesSearch && matchesParty;
    });
    if (filtered.length > 0) {
      filteredMembersByState[state] = filtered;
    }
  });
  
  const filteredStates = Object.keys(filteredMembersByState);
  
  const getSelectedCount = (state) => {
    return (selectedIds[state] || []).length;
  };
  
  const getTotalCount = (state) => {
    return membersByState[state]?.length || 0;
  };
  
  const getFilteredCount = (state) => {
    return filteredMembersByState[state]?.length || 0;
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[80vh] rounded shadow-lg overflow-hidden" onClick={(e)=>e.stopPropagation()}>
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-lg">
                Select {config.target_level === "federal" ? "Senators" : "Upper House Members (MLCs)"}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                You must select at least one member. Only selected members will receive emails.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
          </div>
          
          {(() => {
            // Check if ANY state has more than 10 selections
            const maxPerState = Math.max(0, ...Object.values(selectedIds).map(arr => arr.length));
            if (maxPerState > 10) {
              return (
                <div className="mb-3 p-3 rounded text-sm" style={{ backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                  <div className="font-semibold">⚠️ To ensure best results, select fewer than 10 contacts per state</div>
                  <details className="mt-1 cursor-pointer">
                    <summary className="text-xs opacity-75 hover:opacity-100">Why does this matter?</summary>
                    <p className="mt-1 text-xs opacity-90">
                      Email clients have limits on mailto link length. With many recipients from one state, the "Send Now" button may produce a shorter email than displayed. 
                      The full email is always available via copy/paste. (Selections across different states don't affect each other since users only see their state's representatives.)
                    </p>
                  </details>
                </div>
              );
            }
            return null;
          })()}
          
          <div>
            <div className="relative">
              <Input
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
              <span className="absolute left-2 top-2.5 text-gray-400">🔍</span>
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Filter by Party</label>
                {selectedParty !== "all" && onSelectAllParty && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => onSelectAllParty(selectedParty)}
                  >
                    ✓ Select All {partyNames[selectedParty] || selectedParty} Members
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedParty("all")}
                  className={`px-3 py-1 text-sm rounded ${
                    selectedParty === "all" 
                      ? "bg-blue-600 text-white" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  All Parties
                </button>
                {allParties.map(party => (
                  <button
                    key={party}
                    onClick={() => setSelectedParty(party)}
                    className={`px-3 py-1 text-sm rounded ${
                      selectedParty === party 
                        ? "bg-blue-600 text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {partyNames[party] || party}
                  </button>
                ))}
              </div>
            </div>
            
            {(searchTerm || selectedParty !== "all") && filteredStates.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                Found {Object.values(filteredMembersByState).flat().length} member(s)
                {searchTerm && ` matching "${searchTerm}"`}
                {selectedParty !== "all" && ` from ${partyNames[selectedParty] || selectedParty}`}
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading members...</div>
          ) : filteredStates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? `No members found matching "${searchTerm}"` : "No members available"}
            </div>
          ) : (
            <div className="space-y-6">
              {filteredStates.map(state => (
                <div key={state} className="border rounded p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-medium">
                      {state} ({getSelectedCount(state)} of {getTotalCount(state)} selected)
                      {searchTerm && (
                        <span className="text-sm text-gray-500 ml-2">
                          - showing {getFilteredCount(state)}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => onSelectAll(state, selectedParty !== "all" ? selectedParty : null)}
                        title={selectedParty !== "all" ? `Select all ${partyNames[selectedParty]} members in ${state}` : `Select all members in ${state}`}
                      >
                        Select All{selectedParty !== "all" ? ` ${partyNames[selectedParty] || selectedParty}` : ""}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onClearAll(state)}>
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {filteredMembersByState[state].map(member => {
                      const isSelected = (selectedIds[state] || []).includes(member.id);
                      const partyDisplay = partyNames[member.party] || member.party || 'IND';
                      return (
                        <label key={member.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggle(member.id, state)}
                          />
                          <div className="flex-1">
                            <div className="text-sm">{member.name}</div>
                            <div className="text-xs text-gray-500">{partyDisplay}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {Object.values(selectedIds).flat().length > 0 
              ? `${Object.values(selectedIds).flat().length} members selected` 
              : "⚠️ No members selected - you must select at least one"}
          </div>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

export default function Campaigns(){
  // Pull org-level brand colours so new campaigns inherit them automatically.
  const _orgInfoForDefaults = (() => { try { return JSON.parse(localStorage.getItem("orgInfo")||"null") || {}; } catch { return {}; } })();
  const _orgTheme = (_orgInfoForDefaults && _orgInfoForDefaults.theme) || {};
  const defaultCampaignTheme = {
    show_logo: _orgTheme.show_logo !== false,
    show_title: _orgTheme.show_title !== false,
    font: _orgTheme.font || 'Inter',
    font_title: _orgTheme.font_title || _orgTheme.font || 'Inter',
    primary: _orgTheme.primary || '#0a2a66',
    primaryText: _orgTheme.primaryText || '#ffffff',
    secondary: _orgTheme.secondary || '#eef2ff',
    secondaryText: _orgTheme.secondaryText || '#0a2a66',
    fieldText: _orgTheme.fieldText || '#0a0a0a',
    fieldBorder: _orgTheme.fieldBorder || '#d1d5db',
    fieldInterior: _orgTheme.fieldInterior || '#ffffff',
    titleColor: _orgTheme.titleColor || _orgTheme.text || '#0a0a0a',
    bg: _orgTheme.bg || '#ffffff',
    text: _orgTheme.text || '#000000',
    scrollbarColor: _orgTheme.primary || '#0a2a66',
    buttonColor: _orgTheme.secondary || '#eef2ff',
  };
  const [list,setList]=useState([]);
  const [loading,setLoading]=useState(true);
  // Design drawer state — { mode: 'create'|'edit', panel: 'logo'|'title'|...|null }
  const [drawerPanel, setDrawerPanel] = useState(null);
  const [drawerTarget, setDrawerTarget] = useState('create'); // 'create' or campaign id of edit modal
  const [form,setForm]=useState({ 
    campaign_type: "set_and_send",
    title:"", 
    subtitle:"",
    show_subtitle:false,
    description:"", 
    example_email:"", 
    talking_points:"", 
    recipients:"", 
    data_collection:false, 
    require_email:false, 
    require_name:false, 
    personalisation:true,
    show_personalisation:true, // Toggle to show/hide personalisation field
    input_placeholders:{ name:'Your name', email:'Your email', phone:'Your phone number', personalisation:'Optional: Add anything you want the AI to include here.' }, 
    theme: { ...defaultCampaignTheme },
    button_style: { rounded: 'md', size: 'default', alignment: 'center', fullWidth: false }, // New button design options
    mp_ping_config: {
      scope: "national",
      target_level: "federal",
      target_state: "",
      house_filter: "both",
      include_federal_for_state: false,
      selected_senators: {},
      always_include_emails: "",
      always_include_mp_ids: []
    },
    sign_send_config: {
      show_counter: true,
      counter_position: "top",
      show_counter_after: 0,
      counter_display_mode: "count_only",
      signature_goal: 1000,
      recipient_mode: "postcode",
      mp_level: "federal",
      set_recipients: "",
      unique_signatures_only: false
    },
    submission_config: {
      inquiry_name: "",
      preamble_address: "",
      committee_emails: "",
      require_address: false,
      subject_line_requirements: false,
      subject_line_explanation: "",
      subject_line_example: ""
    },
    social_surge_config: {
      platforms: ["facebook", "x"],
      // Facebook config
      example_facebook_posts: "",  // Example posts for AI reference (like example_email)
      facebook_include_link: false,
      facebook_link_url: "",
      // X config  
      x_post_type: "basic",  // "basic" or "quote_tweet"
      example_tweets: "",  // Example tweets for AI reference (like example_email)
      x_quote_tweet_url: ""  // URL of tweet to quote (for quote_tweet type)
    }
  });
  const [editId,setEditId]=useState(null);
  const [edit,setEdit]=useState({ 
    campaign_type: "set_and_send",
    title:"", 
    subtitle:"",
    show_subtitle:false,
    description:"", 
    example_email:"", 
    talking_points:"", 
    recipients:"", 
    data_collection:false, 
    require_email:false, 
    require_name:false,
    personalisation:true,
    show_personalisation:true,
    input_placeholders:{ name:'Your name', email:'Your email', phone:'Your phone number', personalisation:'Optional: Add anything you want the AI to include here.' }, 
    theme: { ...defaultCampaignTheme },
    button_style: { rounded: 'md', size: 'default', alignment: 'center' },
    mp_ping_config: {
      scope: "national",
      target_level: "federal",
      target_state: "",
      house_filter: "both",
      include_federal_for_state: false,
      selected_senators: {},
      always_include_emails: "",
      always_include_mp_ids: []
    },
    sign_send_config: {
      show_counter: true,
      counter_position: "top",
      show_counter_after: 0,
      counter_display_mode: "count_only",
      signature_goal: 1000,
      recipient_mode: "postcode",
      mp_level: "federal",
      set_recipients: ""
    },
    submission_config: {
      inquiry_name: "",
      preamble_address: "",
      committee_emails: "",
      require_address: false
    },
    social_surge_config: {
      platforms: ["facebook", "x"],
      example_facebook_posts: "",
      facebook_include_link: false,
      facebook_link_url: "",
      x_post_type: "basic",
      example_tweets: "",
      x_quote_tweet_url: ""
    }
  });
  const [showPicker,setShowPicker]=useState(false);
  const [embedOpen,setEmbedOpen]=useState(false);
  const [embedCode,setEmbedCode]=useState("");
  const [showInactive,setShowInactive]=useState(false);
  const [senators,setSenators]=useState([]);
  const [showUpperHouseModal,setShowUpperHouseModal]=useState(false);
  const [upperHouseMembers,setUpperHouseMembers]=useState([]);
  const [upperHouseLoading,setUpperHouseLoading]=useState(false);
  const [isEditingUpperHouse,setIsEditingUpperHouse]=useState(false);
  
  // Always Include MP selector state
  const [showAlwaysIncludeModal,setShowAlwaysIncludeModal]=useState(false);
  const [alwaysIncludeMPs,setAlwaysIncludeMPs]=useState([]);
  const [alwaysIncludeLoading,setAlwaysIncludeLoading]=useState(false);
  const [alwaysIncludeSearch,setAlwaysIncludeSearch]=useState("");
  const [alwaysIncludeFilters,setAlwaysIncludeFilters]=useState({ state: "all", level: "all", house: "all" });
  const [isEditingAlwaysInclude,setIsEditingAlwaysInclude]=useState(false);
  
  // Collapsible section states for create form
  const [createSections, setCreateSections] = useState({ setup: true, recipients: false, design: false, placeholders: false });
  // Collapsible section states for edit form
  const [editSections, setEditSections] = useState({ setup: true, recipients: false, design: false, placeholders: false });
  
  const [campaignStats,setCampaignStats]=useState({});  // {campaignId: {views, sent, leads}}
  const [tierInfo,setTierInfo]=useState({ tier: 3, limit: null, active_count: 0, can_create: true });

  const load = async (retryCount = 0)=>{ 
    try{ 
      setLoading(true);
      const r = await api.get("/campaigns"); 
      setList(r.data || []);
      // Cache successful response
      try { localStorage.setItem('campaigns_cache', JSON.stringify(r.data || [])); } catch (cacheErr) { /* Ignore cache errors */ }
      
      // Fetch tier info
      try {
        const tierRes = await api.get("/campaigns/tier-info");
        setTierInfo(tierRes.data);
      } catch (tierErr) {
        console.error("Failed to load tier info:", tierErr);
      }
      
      // Fetch basic stats for each active campaign
      const activeCampaigns = (r.data || []).filter(c => c.active !== false);
      if (activeCampaigns.length > 0) {
        try {
          const statsRes = await api.get("/analytics/summary");
          const statsMap = {};
          (statsRes.data.campaigns || []).forEach(s => {
            statsMap[s.campaign_id] = {
              views: s.views || 0,
              generates: s.generates || 0,
              sent: s.sent || 0,
              leads: s.leads || 0
            };
          });
          setCampaignStats(statsMap);
        } catch (statsErr) {
          console.error("Failed to load campaign stats:", statsErr);
        }
      }
    } catch(e){
      console.error("Failed to load campaigns:", e);
      
      // Try to load from cache if available
      if (retryCount === 0) {
        try {
          const cached = localStorage.getItem('campaigns_cache');
          if (cached) {
            const cachedData = JSON.parse(cached);
            if (cachedData && cachedData.length > 0) {
              setList(cachedData);
              toast.warning("Showing cached campaigns. Retrying connection...");
              // Retry once in the background
              setTimeout(() => load(1), 2000);
              return;
            }
          }
        } catch (cacheErr) { /* Ignore cache parsing errors */ }
      }
      
      // Don't clear the list on error - keep showing existing campaigns
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        // Auth issue - will be handled by interceptor
        toast.error("Authentication error. Please refresh the page.");
        return;
      }
      
      if (list.length === 0) {
        toast.error("Failed to load campaigns. Please refresh the page.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(()=>{ 
    // Refresh org info (including logo) on component mount
    loadOrgTheme().catch(() => {});
    load(); 
  },[]);

  const create = async ()=>{
    const token = localStorage.getItem("token")||"";
    const payload = token? JSON.parse(atob(token.split('.')[1])): {};
    const body = {
      // org_id omitted for clients; backend will enforce current user's org. Owners set org in Orgs & Users when impersonating.
      title: form.title,
      subtitle: form.subtitle,
      show_subtitle: form.show_subtitle,
      show_personalisation: form.show_personalisation,
      description: form.description,
      example_email: form.example_email,
      talking_points: form.talking_points? form.talking_points.split('\n').map(s=>s.trim()).filter(Boolean): [],
      input_requirements: { require_email: !!form.require_email, require_name: !!form.require_name, personalisation: personalisationVisible(form.show_personalisation, form.personalisation) },
      input_placeholders: form.input_placeholders,
      data_collection: form.data_collection,
      theme: form.theme,
      button_style: form.button_style,
      campaign_type: form.campaign_type || "set_and_send",
    };
    
    // Add recipient_source or mp_ping_config based on campaign type
    if (form.campaign_type === "mp_ping") {
      body.mp_ping_config = form.mp_ping_config;
      body.recipient_source = {};  // MP Ping doesn't use static recipients
      // Debug: Log selected senators
      console.log('[CREATE] Selected senators:', form.mp_ping_config.selected_senators);
    } else if (form.campaign_type === "sign_and_send") {
      body.sign_send_config = form.sign_send_config;
      if (form.sign_send_config.recipient_mode === "set_recipients") {
        body.recipient_source = { emails: form.sign_send_config.set_recipients.split(/[;,]/).map(s=>s.trim()).filter(Boolean) };
      } else {
        body.recipient_source = {};
      }
    } else if (form.campaign_type === "submission_builder") {
      body.submission_config = form.submission_config;
      body.recipient_source = {};
      // Submission Builder always requires name, email, phone
      body.input_requirements = { require_email: true, require_name: true, require_phone: true, personalisation: personalisationVisible(form.show_personalisation, form.personalisation) };
    } else if (form.campaign_type === "social_surge") {
      body.social_surge_config = form.social_surge_config;
      body.recipient_source = {};
    } else {
      body.recipient_source = { emails: form.recipients.split(/[;,]/).map(s=>s.trim()).filter(Boolean) };
    }
    
    console.log('[CREATE] Sending campaign:', JSON.stringify(body.mp_ping_config, null, 2));
    await api.post("/campaigns", body);
    toast("Campaign created");
    // Reset form
    setForm({ 
      campaign_type: "set_and_send",
      title:"", 
      description:"", 
      example_email:"", 
      talking_points:"", 
      recipients:"", 
      data_collection:false, 
      require_email:false, 
      require_name:false, 
      personalisation:true, 
      input_placeholders:{ name:'Your name', email:'Your email', personalisation:'Optional personalisation (one or two sentences)' }, 
      theme: { ...defaultCampaignTheme },
      mp_ping_config: {
        scope: "national",
        target_level: "federal",
        target_state: "",
        house_filter: "both",
        include_federal_for_state: false,
        selected_senators: {}
      }
    });
    load();
  };

  const publish = async (id)=>{ 
    // Validate campaign before publishing
    const campaign = list.find(c => c.id === id);
    if (!campaign) return;
    
    // For MP Ping campaigns with upper house included, validate that senators/MLCs are selected
    if (campaign.campaign_type === "mp_ping") {
      const config = campaign.mp_ping_config || {};
      const houseFilter = config.house_filter || "both";
      const selectedSenators = config.selected_senators || {};
      const totalSelected = Object.values(selectedSenators).flat().length;
      
      // If upper house is included (both or upper), validate selection
      if (houseFilter === "both" || houseFilter === "upper") {
        if (totalSelected === 0) {
          toast.error(`Cannot publish: You must select at least one ${config.target_level === "federal" ? "Senator" : "Upper House member (MLC)"}`);
          return;
        }
      }
    }
    
    await api.post(`/campaigns/${id}/publish`); 
    toast("Published"); 
    load(); 
  };
  const del = async (id)=>{ await api.delete(`/campaigns/${id}`); toast("Deleted"); load(); };
  const deactivate = async (id)=>{ await api.post(`/campaigns/${id}/deactivate`); toast("Campaign deactivated"); load(); };
  const activate = async (id)=>{ await api.post(`/campaigns/${id}/activate`); toast("Campaign activated"); load(); };
  
  const loadUpperHouseMembers = async (state, level) => {
    setUpperHouseLoading(true);
    try {
      const params = { level };
      if (state) params.state = state;
      const r = await api.get('/owner/upper-house-members', { params });
      setUpperHouseMembers(r.data.members || []);
    } catch (e) {
      toast.error("Failed to load upper house members");
    } finally {
      setUpperHouseLoading(false);
    }
  };
  
  const openUpperHouseSelector = async (forEdit = false) => {
    setIsEditingUpperHouse(forEdit);
    const config = forEdit ? edit.mp_ping_config : form.mp_ping_config;
    const state = config.scope === "state" ? config.target_state : null;
    const level = config.target_level;
    
    if (!state && config.scope === "state") {
      toast.error("Please select a state first");
      return;
    }
    
    await loadUpperHouseMembers(state, level);
    setShowUpperHouseModal(true);
  };
  
  const toggleUpperHouseMember = (memberId, state) => {
    const config = isEditingUpperHouse ? edit.mp_ping_config : form.mp_ping_config;
    const currentSelections = config.selected_senators || {};
    const stateSelections = currentSelections[state] || [];
    
    let newStateSelections;
    if (stateSelections.includes(memberId)) {
      // Remove
      newStateSelections = stateSelections.filter(id => id !== memberId);
    } else {
      // Add
      newStateSelections = [...stateSelections, memberId];
    }
    
    const newSelections = {
      ...currentSelections,
      [state]: newStateSelections
    };
    
    if (isEditingUpperHouse) {
      setEdit({...edit, mp_ping_config: {...edit.mp_ping_config, selected_senators: newSelections}});
    } else {
      setForm({...form, mp_ping_config: {...form.mp_ping_config, selected_senators: newSelections}});
    }
  };
  
  const selectAllUpperHouse = (state, partyFilter = null) => {
    // Get all members for this state, optionally filtered by party
    const stateMembers = upperHouseMembers.filter(m => {
      const matchesState = m.state === state;
      const matchesParty = !partyFilter || m.party === partyFilter;
      return matchesState && matchesParty;
    });
    const memberIds = stateMembers.map(m => m.id);
    
    const config = isEditingUpperHouse ? edit.mp_ping_config : form.mp_ping_config;
    const newSelections = {
      ...(config.selected_senators || {}),
      [state]: memberIds
    };
    
    if (isEditingUpperHouse) {
      setEdit({...edit, mp_ping_config: {...edit.mp_ping_config, selected_senators: newSelections}});
    } else {
      setForm({...form, mp_ping_config: {...form.mp_ping_config, selected_senators: newSelections}});
    }
  };
  
  const selectAllPartyAcrossStates = (party) => {
    // Select all members of a specific party across all states
    const partyMembers = upperHouseMembers.filter(m => m.party === party);
    const config = isEditingUpperHouse ? edit.mp_ping_config : form.mp_ping_config;
    const newSelections = {...(config.selected_senators || {})};
    
    partyMembers.forEach(member => {
      const state = member.state;
      if (!newSelections[state]) {
        newSelections[state] = [];
      }
      if (!newSelections[state].includes(member.id)) {
        newSelections[state].push(member.id);
      }
    });
    
    if (isEditingUpperHouse) {
      setEdit({...edit, mp_ping_config: {...edit.mp_ping_config, selected_senators: newSelections}});
    } else {
      setForm({...form, mp_ping_config: {...form.mp_ping_config, selected_senators: newSelections}});
    }
    
    toast.success(`Selected all ${partyMembers.length} members from ${party}`);
  };
  
  const clearAllUpperHouse = (state) => {
    const config = isEditingUpperHouse ? edit.mp_ping_config : form.mp_ping_config;
    const newSelections = {
      ...(config.selected_senators || {}),
      [state]: []
    };
    
    if (isEditingUpperHouse) {
      setEdit({...edit, mp_ping_config: {...edit.mp_ping_config, selected_senators: newSelections}});
    } else {
      setForm({...form, mp_ping_config: {...form.mp_ping_config, selected_senators: newSelections}});
    }
  };

  // Always Include MP functions
  const loadAlwaysIncludeMPs = async () => {
    setAlwaysIncludeLoading(true);
    try {
      const params = {};
      if (alwaysIncludeSearch) params.search = alwaysIncludeSearch;
      if (alwaysIncludeFilters.state !== "all") params.state = alwaysIncludeFilters.state;
      if (alwaysIncludeFilters.level !== "all") params.level = alwaysIncludeFilters.level;
      if (alwaysIncludeFilters.house !== "all") params.house = alwaysIncludeFilters.house;
      params.limit = 100;
      
      const r = await api.get('/owner/all-mps', { params });
      setAlwaysIncludeMPs(r.data.members || []);
    } catch (e) {
      toast.error("Failed to load MPs");
    } finally {
      setAlwaysIncludeLoading(false);
    }
  };

  const openAlwaysIncludeSelector = async (forEdit = false) => {
    setIsEditingAlwaysInclude(forEdit);
    setAlwaysIncludeSearch("");
    setAlwaysIncludeFilters({ state: "all", level: "all", house: "all" });
    await loadAlwaysIncludeMPs();
    setShowAlwaysIncludeModal(true);
  };

  const toggleAlwaysIncludeMP = (mpId) => {
    const config = isEditingAlwaysInclude ? edit.mp_ping_config : form.mp_ping_config;
    const currentIds = config.always_include_mp_ids || [];
    
    let newIds;
    if (currentIds.includes(mpId)) {
      newIds = currentIds.filter(id => id !== mpId);
    } else {
      newIds = [...currentIds, mpId];
    }
    
    if (isEditingAlwaysInclude) {
      setEdit({...edit, mp_ping_config: {...edit.mp_ping_config, always_include_mp_ids: newIds}});
    } else {
      setForm({...form, mp_ping_config: {...form.mp_ping_config, always_include_mp_ids: newIds}});
    }
  };

  const clearAlwaysIncludeMPs = () => {
    if (isEditingAlwaysInclude) {
      setEdit({...edit, mp_ping_config: {...edit.mp_ping_config, always_include_mp_ids: []}});
    } else {
      setForm({...form, mp_ping_config: {...form.mp_ping_config, always_include_mp_ids: []}});
    }
  };

  const startEdit = (c)=>{ 
    console.log('[START EDIT] Campaign:', c.id);
    console.log('[START EDIT] mp_ping_config:', JSON.stringify(c.mp_ping_config, null, 2));
    setEditId(c.id);
    // Auto-expand Recipient Settings for campaign types that need it
    if (c.campaign_type === "submission_builder" || c.campaign_type === "mp_ping" || c.campaign_type === "sign_and_send") {
      setEditSections(prev => ({...prev, recipients: true}));
    }
    setEdit({ 
    campaign_type: c.campaign_type || 'set_and_send',
    title:c.title,
    subtitle:c.subtitle || "",
    show_subtitle:!!c.show_subtitle,
    description:c.description,
    example_email:c.example_email,
    talking_points:(c.talking_points||[]).join('\n'),
    recipients:(c.recipient_source?.emails||[]).join(';'),
    data_collection: !!c.data_collection,
    require_email: !!c.input_requirements?.require_email,
    require_name: !!c.input_requirements?.require_name,
    personalisation: !!c.input_requirements?.personalisation,
    show_personalisation: personalisationVisible(c.show_personalisation, c.input_requirements?.personalisation),
    input_placeholders: {
      name: c.input_placeholders?.name || 'Your name',
      email: c.input_placeholders?.email || 'Your email',
      personalisation: c.input_placeholders?.personalisation || 'Optional: Add anything you want the AI to include here.'
    },
    theme: {
      show_logo: c.theme?.show_logo !== false,
      show_title: c.theme?.show_title !== false,
      font: c.theme?.font || 'Inter',
      font_title: c.theme?.font_title || c.theme?.font || 'Inter',
      primary: c.theme?.primary || '#0a2a66',
      primaryText: c.theme?.primaryText || '#ffffff',
      secondary: c.theme?.secondary || '#eef2ff',
      fieldText: c.theme?.fieldText || '#0a0a0a',
      fieldBorder: c.theme?.fieldBorder || '#d1d5db',
      fieldInterior: c.theme?.fieldInterior || '#ffffff',
      titleColor: c.theme?.titleColor || c.theme?.text || '#0a0a0a',
      bg: c.theme?.bg || '#ffffff',
      text: c.theme?.text || '#000000',
      scrollbarColor: c.theme?.scrollbarColor || c.theme?.primary || '#0a2a66',
      buttonColor: c.theme?.buttonColor || c.theme?.secondary || '#eef2ff'
    },
    button_style: {
      rounded: c.button_style?.rounded || 'md',
      size: c.button_style?.size || 'default',
      alignment: c.button_style?.alignment || 'center'
    },
    mp_ping_config: {
      scope: c.mp_ping_config?.scope || "national",
      target_level: c.mp_ping_config?.target_level || "federal",
      target_state: c.mp_ping_config?.target_state || "",
      house_filter: c.mp_ping_config?.house_filter || "both",
      include_federal_for_state: c.mp_ping_config?.include_federal_for_state || false,
      selected_senators: c.mp_ping_config?.selected_senators || {},
      always_include_emails: c.mp_ping_config?.always_include_emails || "",
      always_include_mp_ids: c.mp_ping_config?.always_include_mp_ids || []
    },
    sign_send_config: {
      show_counter: c.sign_send_config?.show_counter ?? true,
      counter_position: c.sign_send_config?.counter_position || "top",
      show_counter_after: c.sign_send_config?.show_counter_after ?? 0,
      counter_display_mode: c.sign_send_config?.counter_display_mode || "count_only",
      signature_goal: c.sign_send_config?.signature_goal || 1000,
      recipient_mode: c.sign_send_config?.recipient_mode || "postcode",
      mp_level: c.sign_send_config?.mp_level || "federal",
      set_recipients: c.sign_send_config?.set_recipients || "",
      unique_signatures_only: c.sign_send_config?.unique_signatures_only ?? false
    },
    submission_config: c.submission_config || {
      inquiry_name: "",
      preamble_address: "",
      committee_emails: "",
      require_address: false,
      subject_line_requirements: false,
      subject_line_explanation: "",
      subject_line_example: ""
    },
    social_surge_config: c.social_surge_config || {
      platforms: ["facebook", "x"],
      example_facebook_posts: "",
      facebook_include_link: false,
      facebook_link_url: "",
      x_post_type: "basic",
      example_tweets: "",
      x_quote_tweet_url: ""
    }
  }); };

  const saveEdit = async ()=>{
    const body = {
      title: edit.title,
      subtitle: edit.subtitle,
      show_subtitle: edit.show_subtitle,
      show_personalisation: edit.show_personalisation !== false,
      description: edit.description,
      example_email: edit.example_email,
      talking_points: edit.talking_points? edit.talking_points.split('\n').map(s=>s.trim()).filter(Boolean): [],
      input_requirements: { require_email: !!edit.require_email, require_name: !!edit.require_name, personalisation: personalisationVisible(edit.show_personalisation, edit.personalisation) },
      input_placeholders: edit.input_placeholders,
      data_collection: !!edit.data_collection,
      theme: edit.theme,
      button_style: edit.button_style,
      campaign_type: edit.campaign_type || "set_and_send",
    };
    
    // Add recipient_source or mp_ping_config based on campaign type
    if (edit.campaign_type === "mp_ping") {
      body.mp_ping_config = edit.mp_ping_config;
      body.recipient_source = {};
      // Debug: Log selected senators
      console.log('[EDIT/SAVE] Selected senators:', edit.mp_ping_config.selected_senators);
    } else if (edit.campaign_type === "sign_and_send") {
      body.sign_send_config = edit.sign_send_config;
      if (edit.sign_send_config.recipient_mode === "set_recipients") {
        body.recipient_source = { emails: edit.sign_send_config.set_recipients.split(/[;,]/).map(s=>s.trim()).filter(Boolean) };
      } else {
        body.recipient_source = {};
      }
    } else if (edit.campaign_type === "submission_builder") {
      body.submission_config = edit.submission_config;
      body.recipient_source = {};
    } else if (edit.campaign_type === "social_surge") {
      body.social_surge_config = edit.social_surge_config;
      body.recipient_source = {};
    } else {
      body.recipient_source = { emails: edit.recipients.split(/[;,]/).map(s=>s.trim()).filter(Boolean) };
    }
    console.log('[EDIT/SAVE] Sending campaign:', JSON.stringify(body.mp_ping_config, null, 2));
    await api.post(`/campaigns/${editId}/save`, body);
    toast("Saved changes");
    setEditId(null);
    load();
  };

  const applyRecipients=(emails)=>{
    const uniq = Array.from(new Set([...(form.recipients? form.recipients.split(/[;,]/).map(s=>s.trim()).filter(Boolean):[]), ...emails]));
    setForm({...form, recipients: uniq.join(',')});
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="campaigns-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }}>Campaigns</h1>
        <p className="text-sm text-gray-500">Create and manage your campaign widgets.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Create Campaign</CardTitle>
            {tierInfo.limit !== null && (
              <div className="text-sm">
                <span className={`px-2 py-1 rounded ${tierInfo.can_create ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {tierInfo.tier_name}: {tierInfo.active_count}/{tierInfo.limit} campaigns
                </span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!tierInfo.can_create ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
              <div className="font-semibold mb-2">⚠️ Campaign Limit Reached</div>
              <p className="text-sm">
                Your {tierInfo.tier_name} plan allows up to {tierInfo.limit} active campaign{tierInfo.limit > 1 ? 's' : ''}. 
                To create a new campaign, please deactivate an existing one or contact us to upgrade your plan.
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {/* Always Visible: Campaign Type, Title, Subtitle */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Campaign Type</label>
                  <select 
                    className="border rounded px-3 py-2 w-full" 
                    value={form.campaign_type} 
                    onChange={(e)=>{
                      const newType = e.target.value;
                      const currentIsDefault = !form.subtitle || Object.values(DEFAULT_SUBTITLES).includes(form.subtitle);
                      const newSubtitle = form.show_subtitle && currentIsDefault ? (DEFAULT_SUBTITLES[newType] || '') : form.subtitle;
                      setForm({...form, campaign_type: newType, subtitle: newSubtitle});
                      // Auto-expand Recipient Settings for campaign types that need configuration there
                      if (newType === "submission_builder" || newType === "mp_ping" || newType === "sign_and_send") {
                        setCreateSections(prev => ({...prev, recipients: true}));
                      }
                    }}
                    data-testid="c-campaign-type"
                  >
                    <option value="set_and_send">Quick Send Campaign</option>
                    <option value="mp_ping">MP Ping Campaign</option>
                    <option value="sign_and_send">Sign & Send Campaign</option>
                    <option value="submission_builder">Submission Builder</option>
                    <option value="social_surge">Social Surge Campaign</option>
                  </select>
                  <div className="text-xs text-gray-500 mt-1">
                    {form.campaign_type === "set_and_send" ? "Traditional campaign with pre-defined recipients" : 
                     form.campaign_type === "submission_builder" ? "Help users write formal submissions to parliamentary committees" :
                     form.campaign_type === "social_surge" ? "Mobilize supporters to share messages on Facebook and/or X" :
                     "Dynamic campaign - users enter postcode to find their local MPs"}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center gap-2">
                    Display Title
                    <span className="cursor-help text-gray-400 hover:text-gray-600" title="The main heading shown at the top of your campaign widget">ⓘ</span>
                  </label>
                  <Input data-testid="c-title" placeholder="Enter your campaign title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} />
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.show_subtitle} onChange={(e)=>{
                      const checked = e.target.checked;
                      const defaultSubtitle = checked && !form.subtitle ? (DEFAULT_SUBTITLES[form.campaign_type] || '') : form.subtitle;
                      setForm({...form, show_subtitle: checked, subtitle: defaultSubtitle});
                    }} />
                    <span>Add subtitle</span>
                    <span className="cursor-help text-gray-400 hover:text-gray-600" title="Use **text** for bold, new lines preserved">ⓘ</span>
                  </label>
                  {form.show_subtitle && (
                    <div className="mt-2 space-y-1">
                      <Textarea data-testid="c-subtitle" placeholder="Enter subtitle (use **text** for bold)" value={form.subtitle} onChange={(e)=>setForm({...form,subtitle:e.target.value})} rows={2} />
                      <div className="text-xs text-gray-500">Tip: Use **text** for <strong>bold</strong>. New lines preserved.</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Collapsible: Campaign Set Up */}
              <CollapsibleSection title="Campaign Set Up" isOpen={createSections.setup} onToggle={() => setCreateSections({...createSections, setup: !createSections.setup})}>
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center gap-2">
                    Description
                    <span className="cursor-help text-gray-400 hover:text-gray-600" title="Brief explanation of campaign aims for the AI">ⓘ</span>
                  </label>
                  <Textarea data-testid="c-desc" placeholder="Describe your campaign" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} />
                </div>
                
                {form.campaign_type !== "social_surge" && (
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Write an Example Email
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Sample email the AI will personalize">ⓘ</span>
                    </label>
                    <Textarea data-testid="c-example" placeholder="Write your example email here" value={form.example_email} onChange={(e)=>setForm({...form,example_email:e.target.value})} />
                  </div>
                )}
                
                {form.campaign_type !== "social_surge" && (
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Talking Points
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Key points to include, one per line">ⓘ</span>
                    </label>
                    <Textarea data-testid="c-points" placeholder="Enter talking points (one per line)" value={form.talking_points} onChange={(e)=>setForm({...form,talking_points:e.target.value})} />
                  </div>
                )}

                {/* Personalisation Field - moved here */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Personalisation Field</label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input data-testid="create-show-personalisation" type="checkbox" checked={form.show_personalisation} onChange={(e)=>setForm({...form, show_personalisation: e.target.checked, personalisation: e.target.checked})} />
                      <span>Show on widget</span>
                    </label>
                  </div>
                  {form.show_personalisation && (
                    <Input placeholder="Personalisation placeholder text" value={form.input_placeholders?.personalisation || ''} onChange={(e)=>setForm({...form, input_placeholders:{...form.input_placeholders, personalisation:e.target.value}})} />
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 text-sm border-t pt-3">
                  <label className="flex items-center gap-2"><input type="checkbox" data-testid="c-collect" checked={form.data_collection} onChange={(e)=>setForm({...form,data_collection:e.target.checked})} /> Data collection</label>
                  {form.campaign_type !== "submission_builder" && (
                    <>
                      <label className="flex items-center gap-2"><input type="checkbox" data-testid="c-req-email" checked={form.require_email} onChange={(e)=>setForm({...form,require_email:e.target.checked})} /> Require email</label>
                      <label className="flex items-center gap-2"><input type="checkbox" data-testid="c-req-name" checked={form.require_name} onChange={(e)=>setForm({...form,require_name:e.target.checked})} /> Require name</label>
                    </>
                  )}
                </div>

                {/* Social Surge Config */}
                {form.campaign_type === "social_surge" && (
                  <div className="border-t pt-3 space-y-3">
                    <div className="font-medium text-sm text-purple-900">Social Surge Configuration</div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Platforms</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={form.social_surge_config.platforms?.includes("facebook")} onChange={(e)=>{
                            const platforms = e.target.checked ? [...(form.social_surge_config.platforms||[]), "facebook"] : (form.social_surge_config.platforms||[]).filter(p=>p!=="facebook");
                            setForm({...form, social_surge_config:{...form.social_surge_config, platforms}});
                          }} />
                          Facebook
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={form.social_surge_config.platforms?.includes("x")} onChange={(e)=>{
                            const platforms = e.target.checked ? [...(form.social_surge_config.platforms||[]), "x"] : (form.social_surge_config.platforms||[]).filter(p=>p!=="x");
                            setForm({...form, social_surge_config:{...form.social_surge_config, platforms}});
                          }} />
                          X (Twitter)
                        </label>
                      </div>
                    </div>
                    {form.social_surge_config.platforms?.includes("facebook") && (
                      <div className="space-y-2 p-3 bg-blue-50 rounded">
                        <div className="font-medium text-sm text-blue-900">Facebook Settings</div>
                        <Textarea placeholder="Example Facebook posts for AI reference..." value={form.social_surge_config.example_facebook_posts} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, example_facebook_posts:e.target.value}})} rows={2} />
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={form.social_surge_config.facebook_include_link} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, facebook_include_link:e.target.checked}})} />
                          Include link in posts
                        </label>
                        {form.social_surge_config.facebook_include_link && (
                          <Input placeholder="Link URL" value={form.social_surge_config.facebook_link_url} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, facebook_link_url:e.target.value}})} />
                        )}
                      </div>
                    )}
                    {form.social_surge_config.platforms?.includes("x") && (
                      <div className="space-y-2 p-3 bg-gray-100 rounded">
                        <div className="font-medium text-sm">X (Twitter) Settings</div>
                        <select className="w-full border rounded p-2 text-sm" value={form.social_surge_config.x_post_type} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, x_post_type:e.target.value}})}>
                          <option value="basic">Basic Post</option>
                          <option value="quote_tweet">Quote Tweet</option>
                          <option value="reply">Reply</option>
                        </select>
                        <Textarea placeholder="Example tweets for AI reference..." value={form.social_surge_config.example_tweets} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, example_tweets:e.target.value}})} rows={2} maxLength={280} />
                        {(form.social_surge_config.x_post_type === "quote_tweet" || form.social_surge_config.x_post_type === "reply") && (
                          <Input placeholder="Tweet URL to quote/reply to" value={form.social_surge_config.x_quote_tweet_url} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, x_quote_tweet_url:e.target.value}})} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CollapsibleSection>

              {/* Collapsible: Recipient Settings */}
              <CollapsibleSection title="Recipient Settings" isOpen={createSections.recipients} onToggle={() => setCreateSections({...createSections, recipients: !createSections.recipients})}>
                {form.campaign_type === "set_and_send" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      Recipient Emails
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Comma-separated email addresses">ⓘ</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Textarea data-testid="c-recipients" placeholder="Enter email addresses (comma-separated)" value={form.recipients} onChange={(e)=>setForm({...form,recipients:e.target.value})} />
                      <Button data-testid="c-pick-recipients" variant="secondary" onClick={()=>setShowPicker(true)}>Pick from Directory</Button>
                    </div>
                    {(() => {
                      const recipientCount = (form.recipients || '').split(/[,;]/).filter(e => e.trim()).length;
                      if (recipientCount > 10) {
                        return (
                          <div className="p-3 rounded text-sm bg-red-50 text-red-800 border border-red-200">
                            ⚠️ {recipientCount} recipients selected. For best results, select fewer than 10.
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {form.campaign_type === "mp_ping" && (
                  <div className="space-y-3">
                    <div className="font-medium text-sm text-blue-900">MP Ping Configuration</div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Campaign Scope</label>
                      <select className="w-full border rounded p-2 text-sm" value={form.mp_ping_config.scope} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, scope:e.target.value, target_state: e.target.value === "national" ? "" : form.mp_ping_config.target_state}})}>
                        <option value="national">National (All States)</option>
                        <option value="state">Single State</option>
                      </select>
                    </div>
                    {form.mp_ping_config.scope === "state" && (
                      <div>
                        <label className="text-sm font-medium mb-1 block">Target State</label>
                        <select className="w-full border rounded p-2 text-sm" value={form.mp_ping_config.target_state} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, target_state:e.target.value}})}>
                          <option value="">Select State</option>
                          {["ACT","NSW","NT","QLD","SA","TAS","VIC","WA"].map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium mb-1 block">Target MPs</label>
                      <select className="w-full border rounded p-2 text-sm" value={form.mp_ping_config.target_level} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, target_level:e.target.value}})}>
                        <option value="federal">Federal MPs</option>
                        <option value="state">State MPs</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">House Filter</label>
                      <select className="w-full border rounded p-2 text-sm" value={form.mp_ping_config.house_filter} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, house_filter:e.target.value}})}>
                        <option value="both">Both Houses</option>
                        <option value="lower">Lower House Only</option>
                        <option value="upper">Upper House Only</option>
                      </select>
                    </div>
                    {(form.mp_ping_config.house_filter === "both" || form.mp_ping_config.house_filter === "upper") && (
                      <Button variant="outline" size="sm" onClick={()=>{setIsEditingUpperHouse(false); loadUpperHouseMembers(form.mp_ping_config.target_state, form.mp_ping_config.target_level); setShowUpperHouseModal(true);}}>
                        Select {form.mp_ping_config.target_level === "federal" ? "Senators" : "Upper House Members"} ({Object.values(form.mp_ping_config.selected_senators || {}).flat().length} selected)
                      </Button>
                    )}
                    <div>
                      <label className="text-sm font-medium mb-1 block">Always Include (optional)</label>
                      <Textarea placeholder="Additional emails to always include (comma-separated)" value={form.mp_ping_config.always_include_emails} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, always_include_emails:e.target.value}})} rows={2} />
                    </div>
                  </div>
                )}

                {form.campaign_type === "sign_and_send" && (
                  <div className="space-y-3">
                    <div className="font-medium text-sm text-green-900">Sign & Send Configuration</div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.sign_send_config.show_counter} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, show_counter:e.target.checked}})} />
                      Show signature counter
                    </label>
                    {form.sign_send_config.show_counter && (
                      <>
                        <select className="w-full border rounded p-2 text-sm" value={form.sign_send_config.counter_position} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, counter_position:e.target.value}})}>
                          <option value="top">Counter at Top</option>
                          <option value="bottom">Counter at Bottom</option>
                        </select>
                        <select className="w-full border rounded p-2 text-sm" value={form.sign_send_config.counter_display_mode} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, counter_display_mode:e.target.value}})}>
                          <option value="count_only">Count Only</option>
                          <option value="progress">Progress Bar</option>
                        </select>
                        {form.sign_send_config.counter_display_mode === "progress" && (
                          <Input type="number" placeholder="Signature Goal" value={form.sign_send_config.signature_goal} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, signature_goal:parseInt(e.target.value)||1000}})} />
                        )}
                      </>
                    )}
                    <div>
                      <label className="text-sm font-medium mb-1 block">Recipient Mode</label>
                      <select className="w-full border rounded p-2 text-sm" value={form.sign_send_config.recipient_mode} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, recipient_mode:e.target.value}})}>
                        <option value="postcode">Find by Postcode</option>
                        <option value="set_recipients">Set Recipients</option>
                      </select>
                    </div>
                    {form.sign_send_config.recipient_mode === "set_recipients" && (
                      <Textarea placeholder="Recipient emails (comma-separated)" value={form.sign_send_config.set_recipients} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, set_recipients:e.target.value}})} />
                    )}
                    {form.sign_send_config.recipient_mode === "postcode" && (
                      <select className="w-full border rounded p-2 text-sm" value={form.sign_send_config.mp_level} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, mp_level:e.target.value}})}>
                        <option value="federal">Federal MP</option>
                        <option value="state">State MP</option>
                      </select>
                    )}
                  </div>
                )}

                {form.campaign_type === "submission_builder" && (
                  <div className="space-y-3">
                    <div className="font-medium text-sm text-amber-900">Submission Builder Configuration</div>
                    <Input placeholder="Inquiry Name *" value={form.submission_config.inquiry_name} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, inquiry_name:e.target.value}})} />
                    
                    {/* Parliament Address Presets */}
                    <div>
                      <label className="text-sm font-medium mb-1 block">Preamble Address</label>
                      <select 
                        className="border rounded px-3 py-2 w-full mb-2 text-sm"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            setForm({...form, submission_config:{...form.submission_config, preamble_address: e.target.value}});
                          }
                        }}
                      >
                        <option value="">-- Select Parliament (or type below) --</option>
                        <optgroup label="Federal">
                          <option value={"Senate of Australia\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"}>Senate of Australia</option>
                          <option value={"House of Representatives\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"}>House of Representatives</option>
                          <option value={"Parliament of Australia\nParliament House\nCanberra ACT 2600\nAUSTRALIA"}>Parliament of Australia</option>
                        </optgroup>
                        <optgroup label="Western Australia">
                          <option value={"Legislative Assembly of Western Australia\nParliament House\n4 Harvest Terrace\nWEST PERTH WA 6005"}>Legislative Assembly of WA</option>
                          <option value={"Legislative Council of Western Australia\nParliament House\n4 Harvest Terrace\nWEST PERTH WA 6005"}>Legislative Council of WA</option>
                        </optgroup>
                        <optgroup label="New South Wales">
                          <option value={"New South Wales Parliament\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>NSW Parliament</option>
                          <option value={"Legislative Assembly of New South Wales\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>Legislative Assembly of NSW</option>
                          <option value={"Legislative Council of New South Wales\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>Legislative Council of NSW</option>
                        </optgroup>
                        <optgroup label="Victoria">
                          <option value={"Parliament of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Parliament of Victoria</option>
                          <option value={"Legislative Assembly of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Legislative Assembly of Victoria</option>
                          <option value={"Legislative Council of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Legislative Council of Victoria</option>
                        </optgroup>
                        <optgroup label="Queensland">
                          <option value={"Parliament of Queensland\nParliament House\nCnr George and Alice Streets\nBRISBANE QLD 4000"}>Parliament of Queensland</option>
                          <option value={"Legislative Assembly of Queensland\nParliament House\nCnr George and Alice Streets\nBRISBANE QLD 4000"}>Legislative Assembly of Queensland</option>
                        </optgroup>
                        <optgroup label="South Australia">
                          <option value={"Parliament of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>Parliament of South Australia</option>
                          <option value={"House of Assembly of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>House of Assembly of SA</option>
                          <option value={"Legislative Council of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>Legislative Council of SA</option>
                        </optgroup>
                        <optgroup label="Tasmania">
                          <option value={"Parliament of Tasmania\nParliament House\nHobart TAS 7000"}>Parliament of Tasmania</option>
                          <option value={"House of Assembly of Tasmania\nParliament House\nHobart TAS 7000"}>House of Assembly of Tasmania</option>
                          <option value={"Legislative Council of Tasmania\nParliament House\nHobart TAS 7000"}>Legislative Council of Tasmania</option>
                        </optgroup>
                        <optgroup label="Territories">
                          <option value={"Parliament of the Australian Capital Territory\nACT Legislative Assembly\n196 London Circuit\nCANBERRA ACT 2601"}>ACT Legislative Assembly</option>
                          <option value={"Parliament of the Northern Territory\nLegislative Assembly of the Northern Territory\nParliament House\n15 Mitchell Street\nDARWIN NT 0800"}>NT Legislative Assembly</option>
                        </optgroup>
                      </select>
                      <Textarea placeholder="Preamble Address (optional)" value={form.submission_config.preamble_address} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, preamble_address:e.target.value}})} rows={3} />
                      <div className="text-xs text-gray-500 mt-1">Select from dropdown or edit manually</div>
                    </div>
                    
                    <Input placeholder="Committee Email(s) *" value={form.submission_config.committee_emails} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, committee_emails:e.target.value}})} />
                    
                    {/* Subject Line AI Instructions */}
                    <div className="border rounded p-3 bg-gray-50 space-y-2">
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input 
                          type="checkbox" 
                          checked={form.submission_config.subject_line_requirements || false} 
                          onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, subject_line_requirements:e.target.checked}})} 
                        />
                        Custom subject line format
                      </label>
                      {form.submission_config.subject_line_requirements && (
                        <>
                          <Textarea 
                            placeholder="Explain how the subject line should be formatted (e.g., 'Include the submission number from our system, the inquiry short name, and the submitter's name')" 
                            value={form.submission_config.subject_line_explanation || ''} 
                            onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, subject_line_explanation:e.target.value}})} 
                            rows={2} 
                          />
                          <Input 
                            placeholder="Example subject line (e.g., 'SUB-0091: Tax Inquiry - John Smith')" 
                            value={form.submission_config.subject_line_example || ''} 
                            onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, subject_line_example:e.target.value}})} 
                          />
                          <div className="text-xs text-gray-500">AI will follow these instructions when generating the subject line</div>
                        </>
                      )}
                    </div>
                    
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.submission_config.require_address} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, require_address:e.target.checked}})} />
                      Require submitter address
                    </label>
                  </div>
                )}
              </CollapsibleSection>

              {/* Design moved to the floating Drawer — click any element in
                  the preview, or the button below, to open it. */}
              <button
                type="button"
                data-testid="c-open-design"
                onClick={() => { setDrawerTarget('create'); setDrawerPanel('brand'); }}
                className="w-full border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-700 hover:border-[#1E3A8A] hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A] transition flex items-center justify-between"
              >
                <span><span className="font-medium">Design</span> — fonts, colours, logo, title, fields, button & background</span>
                <span className="text-xs opacity-70">Open ✎</span>
              </button>

              {/* Collapsible: Placeholder Text */}
              <CollapsibleSection title="Placeholder Text" isOpen={createSections.placeholders} onToggle={() => setCreateSections({...createSections, placeholders: !createSections.placeholders})}>
                <div className="flex gap-2">
                  <Input placeholder="Name placeholder" value={form.input_placeholders?.name || ''} onChange={(e)=>setForm({...form, input_placeholders:{...form.input_placeholders, name:e.target.value}})} />
                  <Input placeholder="Email placeholder" value={form.input_placeholders?.email || ''} onChange={(e)=>setForm({...form, input_placeholders:{...form.input_placeholders, email:e.target.value}})} />
                </div>
              </CollapsibleSection>

              <Button data-testid="c-create" onClick={create} className="w-full">Create Campaign</Button>
            </div>
            <WidgetPreview title={form.title} subtitle={form.subtitle} showSubtitle={form.show_subtitle} theme={form.theme} requireEmail={form.require_email} requireName={form.require_name} personalise={form.personalisation} showPersonalisation={form.show_personalisation} dataCollection={form.data_collection} recipients={form.recipients} inputPlaceholders={form.input_placeholders} campaignType={form.campaign_type} signSendConfig={form.sign_send_config} submissionConfig={form.submission_config} socialSurgeConfig={form.social_surge_config} buttonStyle={form.button_style} onPatch={(field, value) => setForm({ ...form, [field]: value, ...(field === 'subtitle' && value ? { show_subtitle: true } : {}) })} onButtonStyleChange={(bs) => setForm({ ...form, button_style: bs })} onConfigPatch={(cfg, key, value) => setForm(prev => ({ ...prev, [cfg]: { ...(prev[cfg] || {}), [key]: value } }))} onOpenDrawer={(panel) => { setDrawerTarget('create'); setDrawerPanel(panel); }} />
          </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active Campaigns ({list.filter(c => c.active !== false).length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  {['title','status','actions'].map(h=> (<TableHead key={h}>{h}</TableHead>))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <HighTrafficLoading />
                    </TableCell>
                  </TableRow>
                ) : list.filter(c => c.active !== false).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                      No active campaigns. Create one above to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  list.filter(c => c.active !== false).map((c)=> (
                    <TableRow key={c.id} data-testid={`c-row-${c.id}`}>
                      <TableCell>{c.title}</TableCell>
                      <TableCell>{c.status}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {c.status!=="published" && (
                            <Button size="sm" data-testid={`c-publish-${c.id}`} onClick={()=>publish(c.id)}>Publish</Button>
                          )}
                          <Button size="sm" variant="secondary" data-testid={`c-edit-${c.id}`} onClick={()=>startEdit(c)}>Edit</Button>
                          <Button size="sm" variant="outline" data-testid={`c-analytics-${c.id}`} onClick={()=>{window.location.href=`/analytics/${c.id}`}}>Analytics</Button>
                          <a data-testid={`c-open-${c.id}`} className="underline text-blue-600 text-sm" href={`/widget/${c.id}`} target="_blank" rel="noreferrer">Open widget</a>
                          <Button size="sm" variant="outline" data-testid={`c-embed-${c.id}`} onClick={()=>{
                            setEmbedCode(c.id);
                            setEmbedOpen(true);
                          }}>Copy Embed Code</Button>
                          <Button size="sm" variant="outline" data-testid={`c-deactivate-${c.id}`} onClick={()=>deactivate(c.id)}>Deactivate</Button>
                          <span className="ml-2 border-l pl-3 flex gap-2 text-xs text-gray-500">
                            <span title="Emails generated">📧 {campaignStats[c.id]?.generates || 0}</span>
                            <span title="Leads">👤 {campaignStats[c.id]?.leads || 0}</span>
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between cursor-pointer" onClick={()=>setShowInactive(!showInactive)}>
            <span>Inactive Campaigns ({list.filter(c => c.active === false).length})</span>
            <Button variant="ghost" size="sm">
              {showInactive ? '▼ Collapse' : '▶ Expand'}
            </Button>
          </CardTitle>
        </CardHeader>
        {showInactive && (
          <CardContent>
            <div className="overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    {['title','status','actions'].map(h=> (<TableHead key={h}>{h}</TableHead>))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <HighTrafficLoading />
                      </TableCell>
                    </TableRow>
                  ) : list.filter(c => c.active === false).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                        No inactive campaigns.
                      </TableCell>
                    </TableRow>
                  ) : (
                    list.filter(c => c.active === false).map((c)=> (
                      <TableRow key={c.id} data-testid={`c-row-inactive-${c.id}`} className="bg-gray-50">
                        <TableCell className="text-gray-600">{c.title}</TableCell>
                        <TableCell className="text-gray-600">{c.status}</TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="secondary" data-testid={`c-activate-${c.id}`} onClick={()=>activate(c.id)}>Activate</Button>
                          <Button size="sm" variant="outline" data-testid={`c-analytics-${c.id}`} onClick={()=>{window.location.href=`/analytics/${c.id}`}}>Analytics</Button>
                          <Button size="sm" variant="destructive" data-testid={`c-delete-${c.id}`} onClick={()=>del(c.id)}>Delete</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        )}
      </Card>

      {editId && (
        <Card>
          <CardHeader><CardTitle>Edit Campaign</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {/* Always Visible: Title, Subtitle */}
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Display Title
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="The main heading shown at the top of your campaign widget">ⓘ</span>
                    </label>
                    <Input data-testid="e-title" placeholder="Enter your campaign title" value={edit.title} onChange={(e)=>setEdit({...edit,title:e.target.value})} />
                  </div>
                  
                  <div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={edit.show_subtitle} onChange={(e)=>{
                        const checked = e.target.checked;
                        const defaultSubtitle = checked && !edit.subtitle ? (DEFAULT_SUBTITLES[edit.campaign_type] || '') : edit.subtitle;
                        setEdit({...edit, show_subtitle: checked, subtitle: defaultSubtitle});
                      }} />
                      <span>Add subtitle</span>
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Use **text** for bold, new lines preserved">ⓘ</span>
                    </label>
                    {edit.show_subtitle && (
                      <div className="mt-2 space-y-1">
                        <Textarea data-testid="e-subtitle" placeholder="Enter subtitle (use **text** for bold)" value={edit.subtitle} onChange={(e)=>setEdit({...edit,subtitle:e.target.value})} rows={2} />
                        <div className="text-xs text-gray-500">Tip: Use **text** for <strong>bold</strong>. New lines preserved.</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Collapsible: Campaign Set Up */}
                <CollapsibleSection title="Campaign Set Up" isOpen={editSections.setup} onToggle={() => setEditSections({...editSections, setup: !editSections.setup})}>
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Description
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Brief explanation of campaign aims for the AI">ⓘ</span>
                    </label>
                    <Textarea data-testid="e-desc" placeholder="Describe your campaign" value={edit.description} onChange={(e)=>setEdit({...edit,description:e.target.value})} />
                  </div>
                  
                  {edit.campaign_type !== "social_surge" && (
                    <div>
                      <label className="text-sm font-medium mb-1 flex items-center gap-2">
                        Write an Example Email
                        <span className="cursor-help text-gray-400 hover:text-gray-600" title="Sample email the AI will personalize">ⓘ</span>
                      </label>
                      <Textarea data-testid="e-example" placeholder="Write your example email here" value={edit.example_email} onChange={(e)=>setEdit({...edit,example_email:e.target.value})} />
                    </div>
                  )}
                  
                  {edit.campaign_type !== "social_surge" && (
                    <div>
                      <label className="text-sm font-medium mb-1 flex items-center gap-2">
                        Talking Points
                        <span className="cursor-help text-gray-400 hover:text-gray-600" title="Key points to include, one per line">ⓘ</span>
                      </label>
                      <Textarea data-testid="e-points" placeholder="Enter talking points (one per line)" value={edit.talking_points} onChange={(e)=>setEdit({...edit,talking_points:e.target.value})} />
                    </div>
                  )}

                  {/* Personalisation Field */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium">Personalisation Field</label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input data-testid="edit-show-personalisation" type="checkbox" checked={edit.show_personalisation !== false} onChange={(e)=>setEdit({...edit, show_personalisation: e.target.checked, personalisation: e.target.checked})} />
                        <span>Show on widget</span>
                      </label>
                    </div>
                    {edit.show_personalisation !== false && (
                      <Input placeholder="Personalisation placeholder text" value={edit.input_placeholders?.personalisation || ''} onChange={(e)=>setEdit({...edit, input_placeholders:{...(edit.input_placeholders||{}), personalisation:e.target.value}})} />
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-sm border-t pt-3">
                    <label className="flex items-center gap-2"><input type="checkbox" data-testid="e-collect" checked={edit.data_collection} onChange={(e)=>setEdit({...edit,data_collection:e.target.checked})} /> Data collection</label>
                    {edit.campaign_type !== "submission_builder" && (
                      <>
                        <label className="flex items-center gap-2"><input type="checkbox" data-testid="e-req-email" checked={edit.require_email} onChange={(e)=>setEdit({...edit,require_email:e.target.checked})} /> Require email</label>
                        <label className="flex items-center gap-2"><input type="checkbox" data-testid="e-req-name" checked={edit.require_name} onChange={(e)=>setEdit({...edit,require_name:e.target.checked})} /> Require name</label>
                      </>
                    )}
                  </div>

                  {/* Social Surge Config */}
                  {edit.campaign_type === "social_surge" && (
                    <div className="border-t pt-3 space-y-3">
                      <div className="font-medium text-sm text-purple-900">Social Surge Configuration</div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Platforms</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={edit.social_surge_config?.platforms?.includes("facebook")} onChange={(e)=>{
                              const platforms = e.target.checked ? [...(edit.social_surge_config?.platforms||[]), "facebook"] : (edit.social_surge_config?.platforms||[]).filter(p=>p!=="facebook");
                              setEdit({...edit, social_surge_config:{...edit.social_surge_config, platforms}});
                            }} />
                            Facebook
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={edit.social_surge_config?.platforms?.includes("x")} onChange={(e)=>{
                              const platforms = e.target.checked ? [...(edit.social_surge_config?.platforms||[]), "x"] : (edit.social_surge_config?.platforms||[]).filter(p=>p!=="x");
                              setEdit({...edit, social_surge_config:{...edit.social_surge_config, platforms}});
                            }} />
                            X (Twitter)
                          </label>
                        </div>
                      </div>
                      {edit.social_surge_config?.platforms?.includes("facebook") && (
                        <div className="space-y-2 p-3 bg-blue-50 rounded">
                          <div className="font-medium text-sm text-blue-900">Facebook Settings</div>
                          <Textarea placeholder="Example Facebook posts..." value={edit.social_surge_config?.example_facebook_posts || ''} onChange={(e)=>setEdit({...edit, social_surge_config:{...edit.social_surge_config, example_facebook_posts:e.target.value}})} rows={2} />
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={edit.social_surge_config?.facebook_include_link} onChange={(e)=>setEdit({...edit, social_surge_config:{...edit.social_surge_config, facebook_include_link:e.target.checked}})} />
                            Include link
                          </label>
                          {edit.social_surge_config?.facebook_include_link && (
                            <Input placeholder="Link URL" value={edit.social_surge_config?.facebook_link_url || ''} onChange={(e)=>setEdit({...edit, social_surge_config:{...edit.social_surge_config, facebook_link_url:e.target.value}})} />
                          )}
                        </div>
                      )}
                      {edit.social_surge_config?.platforms?.includes("x") && (
                        <div className="space-y-2 p-3 bg-gray-100 rounded">
                          <div className="font-medium text-sm">X (Twitter) Settings</div>
                          <select className="w-full border rounded p-2 text-sm" value={edit.social_surge_config?.x_post_type || 'basic'} onChange={(e)=>setEdit({...edit, social_surge_config:{...edit.social_surge_config, x_post_type:e.target.value}})}>
                            <option value="basic">Basic Post</option>
                            <option value="quote_tweet">Quote Tweet</option>
                            <option value="reply">Reply</option>
                          </select>
                          <Textarea placeholder="Example tweets..." value={edit.social_surge_config?.example_tweets || ''} onChange={(e)=>setEdit({...edit, social_surge_config:{...edit.social_surge_config, example_tweets:e.target.value}})} rows={2} maxLength={280} />
                          {(edit.social_surge_config?.x_post_type === "quote_tweet" || edit.social_surge_config?.x_post_type === "reply") && (
                            <Input placeholder="Tweet URL" value={edit.social_surge_config?.x_quote_tweet_url || ''} onChange={(e)=>setEdit({...edit, social_surge_config:{...edit.social_surge_config, x_quote_tweet_url:e.target.value}})} />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CollapsibleSection>

                {/* Collapsible: Recipient Settings */}
                <CollapsibleSection title="Recipient Settings" isOpen={editSections.recipients} onToggle={() => setEditSections({...editSections, recipients: !editSections.recipients})}>
                  {edit.campaign_type === "set_and_send" && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        Recipient Emails
                        <span className="cursor-help text-gray-400 hover:text-gray-600" title="Comma-separated email addresses">ⓘ</span>
                      </label>
                      <Textarea data-testid="e-recipients" placeholder="Enter email addresses (comma-separated)" value={edit.recipients} onChange={(e)=>setEdit({...edit,recipients:e.target.value})} />
                    </div>
                  )}

                  {edit.campaign_type === "mp_ping" && (
                    <div className="space-y-3">
                      <div className="font-medium text-sm text-blue-900">MP Ping Configuration</div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Campaign Scope</label>
                        <select className="w-full border rounded p-2 text-sm" value={edit.mp_ping_config?.scope || 'national'} onChange={(e)=>setEdit({...edit, mp_ping_config:{...edit.mp_ping_config, scope:e.target.value, target_state: e.target.value === "national" ? "" : edit.mp_ping_config?.target_state}})}>
                          <option value="national">National (All States)</option>
                          <option value="state">Single State</option>
                        </select>
                      </div>
                      {edit.mp_ping_config?.scope === "state" && (
                        <div>
                          <label className="text-sm font-medium mb-1 block">Target State</label>
                          <select className="w-full border rounded p-2 text-sm" value={edit.mp_ping_config?.target_state || ''} onChange={(e)=>setEdit({...edit, mp_ping_config:{...edit.mp_ping_config, target_state:e.target.value}})}>
                            <option value="">Select State</option>
                            {["ACT","NSW","NT","QLD","SA","TAS","VIC","WA"].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium mb-1 block">Target MPs</label>
                        <select className="w-full border rounded p-2 text-sm" value={edit.mp_ping_config?.target_level || 'federal'} onChange={(e)=>setEdit({...edit, mp_ping_config:{...edit.mp_ping_config, target_level:e.target.value}})}>
                          <option value="federal">Federal MPs</option>
                          <option value="state">State MPs</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">House Filter</label>
                        <select className="w-full border rounded p-2 text-sm" value={edit.mp_ping_config?.house_filter || 'both'} onChange={(e)=>setEdit({...edit, mp_ping_config:{...edit.mp_ping_config, house_filter:e.target.value}})}>
                          <option value="both">Both Houses</option>
                          <option value="lower">Lower House Only</option>
                          <option value="upper">Upper House Only</option>
                        </select>
                      </div>
                      {(edit.mp_ping_config?.house_filter === "both" || edit.mp_ping_config?.house_filter === "upper") && (
                        <Button variant="outline" size="sm" onClick={()=>{setIsEditingUpperHouse(true); loadUpperHouseMembers(edit.mp_ping_config?.target_state, edit.mp_ping_config?.target_level); setShowUpperHouseModal(true);}}>
                          Select {edit.mp_ping_config?.target_level === "federal" ? "Senators" : "Upper House Members"} ({Object.values(edit.mp_ping_config?.selected_senators || {}).flat().length} selected)
                        </Button>
                      )}
                      <div>
                        <label className="text-sm font-medium mb-1 block">Always Include (optional)</label>
                        <Textarea placeholder="Additional emails (comma-separated)" value={edit.mp_ping_config?.always_include_emails || ''} onChange={(e)=>setEdit({...edit, mp_ping_config:{...edit.mp_ping_config, always_include_emails:e.target.value}})} rows={2} />
                      </div>
                    </div>
                  )}

                  {edit.campaign_type === "sign_and_send" && (
                    <div className="space-y-3">
                      <div className="font-medium text-sm text-green-900">Sign & Send Configuration</div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={edit.sign_send_config?.show_counter ?? true} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, show_counter:e.target.checked}})} />
                        Show signature counter
                      </label>
                      {(edit.sign_send_config?.show_counter ?? true) && (
                        <>
                          <select className="w-full border rounded p-2 text-sm" value={edit.sign_send_config?.counter_position || 'top'} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, counter_position:e.target.value}})}>
                            <option value="top">Counter at Top</option>
                            <option value="bottom">Counter at Bottom</option>
                          </select>
                          <select className="w-full border rounded p-2 text-sm" value={edit.sign_send_config?.counter_display_mode || 'count_only'} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, counter_display_mode:e.target.value}})}>
                            <option value="count_only">Count Only</option>
                            <option value="progress">Progress Bar</option>
                          </select>
                          {edit.sign_send_config?.counter_display_mode === "progress" && (
                            <Input type="number" placeholder="Signature Goal" value={edit.sign_send_config?.signature_goal || 1000} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, signature_goal:parseInt(e.target.value)||1000}})} />
                          )}
                        </>
                      )}
                      <div>
                        <label className="text-sm font-medium mb-1 block">Recipient Mode</label>
                        <select className="w-full border rounded p-2 text-sm" value={edit.sign_send_config?.recipient_mode || 'postcode'} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, recipient_mode:e.target.value}})}>
                          <option value="postcode">Find by Postcode</option>
                          <option value="set_recipients">Set Recipients</option>
                        </select>
                      </div>
                      {edit.sign_send_config?.recipient_mode === "set_recipients" && (
                        <Textarea placeholder="Recipient emails (comma-separated)" value={edit.sign_send_config?.set_recipients || ''} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, set_recipients:e.target.value}})} />
                      )}
                      {edit.sign_send_config?.recipient_mode === "postcode" && (
                        <select className="w-full border rounded p-2 text-sm" value={edit.sign_send_config?.mp_level || 'federal'} onChange={(e)=>setEdit({...edit, sign_send_config:{...edit.sign_send_config, mp_level:e.target.value}})}>
                          <option value="federal">Federal MP</option>
                          <option value="state">State MP</option>
                        </select>
                      )}
                    </div>
                  )}

                  {edit.campaign_type === "submission_builder" && (
                    <div className="space-y-3">
                      <div className="font-medium text-sm text-amber-900">Submission Builder Configuration</div>
                      <Input placeholder="Inquiry Name *" value={edit.submission_config?.inquiry_name || ''} onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, inquiry_name:e.target.value}})} />
                      
                      {/* Parliament Address Presets */}
                      <div>
                        <label className="text-sm font-medium mb-1 block">Preamble Address</label>
                        <select 
                          className="border rounded px-3 py-2 w-full mb-2 text-sm"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              setEdit({...edit, submission_config:{...edit.submission_config, preamble_address: e.target.value}});
                            }
                          }}
                        >
                          <option value="">-- Select Parliament (or type below) --</option>
                          <optgroup label="Federal">
                            <option value={"Senate of Australia\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"}>Senate of Australia</option>
                            <option value={"House of Representatives\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"}>House of Representatives</option>
                            <option value={"Parliament of Australia\nParliament House\nCanberra ACT 2600\nAUSTRALIA"}>Parliament of Australia</option>
                          </optgroup>
                          <optgroup label="Western Australia">
                            <option value={"Legislative Assembly of Western Australia\nParliament House\n4 Harvest Terrace\nWEST PERTH WA 6005"}>Legislative Assembly of WA</option>
                            <option value={"Legislative Council of Western Australia\nParliament House\n4 Harvest Terrace\nWEST PERTH WA 6005"}>Legislative Council of WA</option>
                          </optgroup>
                          <optgroup label="New South Wales">
                            <option value={"New South Wales Parliament\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>NSW Parliament</option>
                            <option value={"Legislative Assembly of New South Wales\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>Legislative Assembly of NSW</option>
                            <option value={"Legislative Council of New South Wales\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>Legislative Council of NSW</option>
                          </optgroup>
                          <optgroup label="Victoria">
                            <option value={"Parliament of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Parliament of Victoria</option>
                            <option value={"Legislative Assembly of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Legislative Assembly of Victoria</option>
                            <option value={"Legislative Council of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Legislative Council of Victoria</option>
                          </optgroup>
                          <optgroup label="Queensland">
                            <option value={"Parliament of Queensland\nParliament House\nCnr George and Alice Streets\nBRISBANE QLD 4000"}>Parliament of Queensland</option>
                            <option value={"Legislative Assembly of Queensland\nParliament House\nCnr George and Alice Streets\nBRISBANE QLD 4000"}>Legislative Assembly of Queensland</option>
                          </optgroup>
                          <optgroup label="South Australia">
                            <option value={"Parliament of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>Parliament of South Australia</option>
                            <option value={"House of Assembly of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>House of Assembly of SA</option>
                            <option value={"Legislative Council of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>Legislative Council of SA</option>
                          </optgroup>
                          <optgroup label="Tasmania">
                            <option value={"Parliament of Tasmania\nParliament House\nHobart TAS 7000"}>Parliament of Tasmania</option>
                            <option value={"House of Assembly of Tasmania\nParliament House\nHobart TAS 7000"}>House of Assembly of Tasmania</option>
                            <option value={"Legislative Council of Tasmania\nParliament House\nHobart TAS 7000"}>Legislative Council of Tasmania</option>
                          </optgroup>
                          <optgroup label="Territories">
                            <option value={"Parliament of the Australian Capital Territory\nACT Legislative Assembly\n196 London Circuit\nCANBERRA ACT 2601"}>ACT Legislative Assembly</option>
                            <option value={"Parliament of the Northern Territory\nLegislative Assembly of the Northern Territory\nParliament House\n15 Mitchell Street\nDARWIN NT 0800"}>NT Legislative Assembly</option>
                          </optgroup>
                        </select>
                        <Textarea placeholder="Preamble Address (optional)" value={edit.submission_config?.preamble_address || ''} onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, preamble_address:e.target.value}})} rows={3} />
                        <div className="text-xs text-gray-500 mt-1">Select from dropdown or edit manually</div>
                      </div>
                      
                      <Input placeholder="Committee Email(s) *" value={edit.submission_config?.committee_emails || ''} onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, committee_emails:e.target.value}})} />
                      
                      {/* Subject Line AI Instructions */}
                      <div className="border rounded p-3 bg-gray-50 space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <input 
                            type="checkbox" 
                            checked={edit.submission_config?.subject_line_requirements || false} 
                            onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, subject_line_requirements:e.target.checked}})} 
                          />
                          Custom subject line format
                        </label>
                        {edit.submission_config?.subject_line_requirements && (
                          <>
                            <Textarea 
                              placeholder="Explain how the subject line should be formatted (e.g., 'Include the submission number from our system, the inquiry short name, and the submitter's name')" 
                              value={edit.submission_config?.subject_line_explanation || ''} 
                              onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, subject_line_explanation:e.target.value}})} 
                              rows={2} 
                            />
                            <Input 
                              placeholder="Example subject line (e.g., 'SUB-0091: Tax Inquiry - John Smith')" 
                              value={edit.submission_config?.subject_line_example || ''} 
                              onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, subject_line_example:e.target.value}})} 
                            />
                            <div className="text-xs text-gray-500">AI will follow these instructions when generating the subject line</div>
                          </>
                        )}
                      </div>
                      
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={edit.submission_config?.require_address} onChange={(e)=>setEdit({...edit, submission_config:{...edit.submission_config, require_address:e.target.checked}})} />
                        Require submitter address
                      </label>
                    </div>
                  )}
                </CollapsibleSection>

                {/* Design moved to the floating Drawer — click any element in
                    the preview, or the button below, to open it. */}
                <button
                  type="button"
                  data-testid="e-open-design"
                  onClick={() => { setDrawerTarget(edit.id || 'edit'); setDrawerPanel('brand'); }}
                  className="w-full border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-700 hover:border-[#1E3A8A] hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A] transition flex items-center justify-between"
                >
                  <span><span className="font-medium">Design</span> — fonts, colours, logo, title, fields, button & background</span>
                  <span className="text-xs opacity-70">Open ✎</span>
                </button>

                {/* Collapsible: Placeholder Text */}
                <CollapsibleSection title="Placeholder Text" isOpen={editSections.placeholders} onToggle={() => setEditSections({...editSections, placeholders: !editSections.placeholders})}>
                  <div className="flex gap-2">
                    <Input placeholder="Name placeholder" value={edit.input_placeholders?.name || ''} onChange={(e)=>setEdit({...edit, input_placeholders:{...(edit.input_placeholders||{}), name:e.target.value}})} />
                    <Input placeholder="Email placeholder" value={edit.input_placeholders?.email || ''} onChange={(e)=>setEdit({...edit, input_placeholders:{...(edit.input_placeholders||{}), email:e.target.value}})} />
                  </div>
                </CollapsibleSection>

                <div className="flex gap-2">
                  <Button data-testid="e-save" onClick={saveEdit}>Save</Button>
                  <Button variant="ghost" data-testid="e-cancel" onClick={()=>setEditId(null)}>Cancel</Button>
                </div>
              </div>
              <WidgetPreview title={edit.title} subtitle={edit.subtitle} showSubtitle={edit.show_subtitle} theme={edit.theme} requireEmail={edit.require_email} requireName={edit.require_name} personalise={edit.personalisation} showPersonalisation={edit.show_personalisation !== false} dataCollection={edit.data_collection} recipients={edit.recipients} inputPlaceholders={edit.input_placeholders} campaignType={edit.campaign_type} signSendConfig={edit.sign_send_config} submissionConfig={edit.submission_config} socialSurgeConfig={edit.social_surge_config} buttonStyle={edit.button_style} onPatch={(field, value) => setEdit({ ...edit, [field]: value, ...(field === 'subtitle' && value ? { show_subtitle: true } : {}) })} onButtonStyleChange={(bs) => setEdit({ ...edit, button_style: bs })} onConfigPatch={(cfg, key, value) => setEdit(prev => ({ ...prev, [cfg]: { ...(prev[cfg] || {}), [key]: value } }))} onOpenDrawer={(panel) => { setDrawerTarget(edit.id || 'edit'); setDrawerPanel(panel); }} />
            </div>
          </CardContent>
        </Card>
      )}

      <RecipientPicker open={showPicker} onClose={()=>setShowPicker(false)} onApply={applyRecipients} />
      <EmbedCodeModal open={embedOpen} id={embedCode} defaultBg="#ffffff" onClose={()=>setEmbedOpen(false)} />
      
      {showUpperHouseModal && (
        <UpperHouseSelectionModal 
          members={upperHouseMembers}
          loading={upperHouseLoading}
          config={isEditingUpperHouse ? edit.mp_ping_config : form.mp_ping_config}
          onToggle={toggleUpperHouseMember}
          onSelectAll={selectAllUpperHouse}
          onClearAll={clearAllUpperHouse}
          onSelectAllParty={selectAllPartyAcrossStates}
          onClose={() => setShowUpperHouseModal(false)}
        />
      )}
      
      {/* Always Include MP Selector Modal */}
      {showAlwaysIncludeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowAlwaysIncludeModal(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[80vh] rounded shadow-lg overflow-hidden" onClick={(e)=>e.stopPropagation()}>
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">Select MPs to Always Include</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    These MPs will be contacted regardless of the user's postcode.
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowAlwaysIncludeModal(false)}>✕</Button>
              </div>
              
              {/* Search and Filters */}
              <div className="space-y-3">
                <Input
                  placeholder="Search by name or electorate..."
                  value={alwaysIncludeSearch}
                  onChange={(e) => setAlwaysIncludeSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadAlwaysIncludeMPs()}
                />
                <div className="flex gap-2 flex-wrap">
                  <select 
                    className="border rounded px-2 py-1 text-sm"
                    value={alwaysIncludeFilters.level}
                    onChange={(e) => setAlwaysIncludeFilters({...alwaysIncludeFilters, level: e.target.value})}
                  >
                    <option value="all">All Levels</option>
                    <option value="federal">Federal</option>
                    <option value="state">State</option>
                  </select>
                  <select 
                    className="border rounded px-2 py-1 text-sm"
                    value={alwaysIncludeFilters.state}
                    onChange={(e) => setAlwaysIncludeFilters({...alwaysIncludeFilters, state: e.target.value})}
                  >
                    <option value="all">All States</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="ACT">ACT</option>
                    <option value="NT">NT</option>
                  </select>
                  <select 
                    className="border rounded px-2 py-1 text-sm"
                    value={alwaysIncludeFilters.house}
                    onChange={(e) => setAlwaysIncludeFilters({...alwaysIncludeFilters, house: e.target.value})}
                  >
                    <option value="all">All Houses</option>
                    <option value="lower">Lower House</option>
                    <option value="upper">Upper House</option>
                  </select>
                  <Button size="sm" onClick={loadAlwaysIncludeMPs}>Search</Button>
                </div>
              </div>
            </div>
            
            <div className="overflow-y-auto" style={{maxHeight: 'calc(80vh - 220px)'}}>
              {alwaysIncludeLoading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : alwaysIncludeMPs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No MPs found. Try adjusting your search.</div>
              ) : (
                <div className="divide-y">
                  {alwaysIncludeMPs.map(mp => {
                    const config = isEditingAlwaysInclude ? edit.mp_ping_config : form.mp_ping_config;
                    const isSelected = (config.always_include_mp_ids || []).includes(mp.id);
                    return (
                      <label 
                        key={mp.id} 
                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <input 
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleAlwaysIncludeMP(mp.id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{mp.name}</div>
                          <div className="text-xs text-gray-500">
                            {mp.electorate} • {mp.state} • {mp.level} ({mp.house}) • {mp.party}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 truncate max-w-[200px]">{mp.email}</div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {(() => {
                  const config = isEditingAlwaysInclude ? edit.mp_ping_config : form.mp_ping_config;
                  const count = (config.always_include_mp_ids || []).length;
                  return count > 0 ? `${count} MP(s) selected` : "No MPs selected";
                })()}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearAlwaysIncludeMPs}>Clear All</Button>
                <Button onClick={() => setShowAlwaysIncludeModal(false)}>Done</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Design drawer — context-sensitive style controls */}
      <DesignDrawer
        open={!!drawerPanel}
        panel={drawerPanel}
        onClose={() => setDrawerPanel(null)}
        theme={drawerTarget === 'create' ? form.theme : (edit?.theme || {})}
        onChange={Object.assign(
          (newTheme) => {
            if (drawerTarget === 'create') setForm(prev => ({ ...prev, theme: newTheme }));
            else setEdit(prev => ({ ...prev, theme: newTheme }));
          },
          { __setPanel: (p) => setDrawerPanel(p) }
        )}
        buttonStyle={drawerTarget === 'create' ? form.button_style : (edit?.button_style || {})}
        onButtonStyleChange={(bs) => {
          if (drawerTarget === 'create') setForm(prev => ({ ...prev, button_style: bs }));
          else setEdit(prev => ({ ...prev, button_style: bs }));
        }}
        subtitle={drawerTarget === 'create' ? form.subtitle : (edit?.subtitle || '')}
        showSubtitle={drawerTarget === 'create' ? !!form.show_subtitle : !!edit?.show_subtitle}
        onPatch={(field, value) => {
          if (drawerTarget === 'create') {
            setForm(prev => ({ ...prev, [field]: value, ...(field === 'subtitle' && value ? { show_subtitle: true } : {}) }));
          } else {
            setEdit(prev => ({ ...prev, [field]: value, ...(field === 'subtitle' && value ? { show_subtitle: true } : {}) }));
          }
        }}
      />

    </div>
  );
}
